import {
    AlertTriangle,
    ArrowUp,
    Briefcase,
    Building,
    CalendarCheck,
    Check,
    ChevronDown,
    Filter,
    Globe,
    Loader2,
    MapPin,
    Mic,
    Phone,
    Star,
    Tags as TagsIcon,
    ThumbsDown,
    ThumbsUp,
    User,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { CaregiverComplianceBadge } from '@apps/web/components/caregivers/caregiver_compliance_badge';
import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { getEhrCaregiverFieldVisibility } from '@apps/web/components/caregivers/ehr_caregiver_field_visibility';
import {
    type EhrCaregiverStatus,
    EhrCaregiverStatusBadge,
} from '@apps/web/components/caregivers/ehr_caregiver_status_badge';
import { RoleBadge } from '@apps/web/components/caregivers/role_badge';
import { ContactBadge } from '@apps/web/components/contact_badge';
import { buildInhouseRecommendationV2RequestParamsFromFacets } from '@apps/web/components/outreaches/inhouse_recommendations_filter_dsl';
import {
    buildEntityDefaultColumnFilters,
    reconcileColumnFiltersAgainstFacets,
} from '@apps/web/components/outreaches/inhouse_recommendations_table_filters';
import { useOutreachV3SelectionState } from '@apps/web/components/outreaches/outreach_v3_selection_state';
import { RecalcShiftDistancesButton } from '@apps/web/components/outreaches/recalc_shift_distances_button';
import { ShiftAvailabilityChips } from '@apps/web/components/outreaches/recommendation_shift_chips';
import {
    isRecommendationFilterTooLarge,
    recommendationErrorMessage,
} from '@apps/web/components/outreaches/utils';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    DataTable,
    DataTableColumnHeader,
} from '@apps/web/components/ui/data_table';
import {
    type CustomFilterConfig,
    DataTableToolbar,
} from '@apps/web/components/ui/data_table_toolbar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { Input } from '@apps/web/components/ui/input';
import { Markdown } from '@apps/web/components/ui/markdown';
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { SearchInput } from '@apps/web/components/ui/search_input';
import { SegmentedControl } from '@apps/web/components/ui/segmented_control';
import { Textarea } from '@apps/web/components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { useMaxDistanceFilter } from '@apps/web/hooks/use_max_distance_filter';
import { useIsMobile } from '@apps/web/hooks/use_mobile';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useDisplayNamesLastFirst } from '@apps/web/hooks/use_person_name_format';
import { useVoiceInput } from '@apps/web/hooks/use_voice_input';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    ELIGIBLE_CAREGIVERS_PAGE_SIZE_OPTIONS,
    formatDistance,
    normalizeEligibleCaregiversPageSize,
} from '@apps/web/lib/callout_utils';
import {
    compareCaregiverDisplayName,
    formatCaregiverName,
} from '@apps/web/lib/caregiver_name';
import { normalizeSelectedCaregiverIds } from '@apps/web/lib/caregiver_shift_selections';
import { formatDateAndTime, formatTime } from '@apps/web/lib/date_utils';
import { EVENTS } from '@apps/web/lib/events';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { useMode } from '@apps/web/lib/mode_store';
import {
    buildRecommendationInitialColumnFilters,
    formatRecommendationDefaultOptionLabel,
    type RecommendationAvailabilityPreference,
    type RecommendationScheduleAvailability,
} from '@apps/web/lib/recommendation_defaults';
import {
    type ColumnDef,
    type ColumnFiltersState,
    type FilterFn,
    type RowSelectionState,
    type SortingState,
    type Table as TableType,
} from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';
import {
    batchUpdateRecommendationCaregiverSelections,
    useEntityFilterOptions,
    useGroupInhouseRecommendations,
    useInhouseRecommendations,
    useOrganization,
    useOrganizationDataSourceAccounts,
    useOrganizationRecommendationDefaults,
    useUnitDismissedCaregivers,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type CaregiverComplianceStatus,
    type CaregiverRole,
    type ShiftAvailabilityRead,
} from '@libraries/typescript/api_client';

const LANGUAGE_NAMES: Record<string, string> = {
    en: 'English',
    es: 'Spanish',
    zh: 'Chinese',
    'zh-CN': 'Chinese (Simplified)',
    'zh-TW': 'Chinese (Traditional)',
    tl: 'Tagalog',
    vi: 'Vietnamese',
    ar: 'Arabic',
    fr: 'French',
    ko: 'Korean',
    ru: 'Russian',
    de: 'German',
    pt: 'Portuguese',
    ja: 'Japanese',
    hi: 'Hindi',
    it: 'Italian',
    pl: 'Polish',
    uk: 'Ukrainian',
    fa: 'Persian',
    bn: 'Bengali',
    ht: 'Haitian Creole',
    el: 'Greek',
    he: 'Hebrew',
    th: 'Thai',
    ur: 'Urdu',
    pa: 'Punjabi',
    gu: 'Gujarati',
    ta: 'Tamil',
    te: 'Telugu',
    mr: 'Marathi',
    ml: 'Malayalam',
    kn: 'Kannada',
    or: 'Odia',
    my: 'Burmese',
    km: 'Khmer',
    lo: 'Lao',
    am: 'Amharic',
    so: 'Somali',
    sw: 'Swahili',
    yo: 'Yoruba',
    ig: 'Igbo',
    ha: 'Hausa',
    zu: 'Zulu',
    af: 'Afrikaans',
    nl: 'Dutch',
    sv: 'Swedish',
    no: 'Norwegian',
    da: 'Danish',
    fi: 'Finnish',
    cs: 'Czech',
    sk: 'Slovak',
    hu: 'Hungarian',
    ro: 'Romanian',
    bg: 'Bulgarian',
    sr: 'Serbian',
    hr: 'Croatian',
    sl: 'Slovenian',
    lt: 'Lithuanian',
    lv: 'Latvian',
    et: 'Estonian',
    tr: 'Turkish',
    az: 'Azerbaijani',
    ka: 'Georgian',
    hy: 'Armenian',
    ne: 'Nepali',
    si: 'Sinhala',
    ms: 'Malay',
    id: 'Indonesian',
    fil: 'Filipino',
    ceb: 'Cebuano',
    hmn: 'Hmong',
    ilo: 'Ilocano',
};
// v6: entries are only written on user-driven filter changes. Older versions
// were stamped with [] on mount (DataTable's storage hook persisted its
// initial empty state before defaults loaded), so a v5 entry cannot be
// trusted to mean "user cleared filters".
const RECOMMENDATION_FILTER_STORAGE_VERSION = 'v6';
// v2 reset: the unversioned key let a column hidden once (even by accident)
// stay hidden forever across every outreach, which surfaced as coordinators
// "missing" scheduled availability. Bumping the key restores defaults once.
const COLUMN_VISIBILITY_KEY = 'phoebe-recs-column-visibility-v2';
const COLUMN_ORDER_KEY = 'phoebe-recs-column-order';
const V2_AVAILABILITY_PREFERENCE_OPTIONS = [
    'available',
    'partial',
    'unavailable',
    'unknown',
] satisfies ReadonlyArray<RecommendationAvailabilityPreference>;
const V2_SCHEDULE_AVAILABILITY_OPTIONS = [
    'available',
    'has_shift_conflict',
] satisfies ReadonlyArray<RecommendationScheduleAvailability>;
const V2_OVERTIME_STATUS_OPTIONS = [
    'within_limit',
    'overtime',
] satisfies ReadonlyArray<string>;
const V2_PREFERRED_OPTIONS = ['preferred'];
const STAR_RATING_COLUMN_ID = 'star_rating';
// Above the 300-caregiver outreach cap so selected mode never paginates.
const SHOW_SELECTED_PAGE_SIZE = 1000;

type VisibleCaregiverNamesById = Readonly<Record<string, string>>;

type InhouseStartOutreachCallback = (
    caregiverIds: Array<string>,
    caregiverNamesById: VisibleCaregiverNamesById,
) => Promise<boolean>;

type InhouseSelectionChangeCallback = (
    caregiverIds: Array<string>,
    caregiverNamesById: VisibleCaregiverNamesById,
) => void;

function formatLanguageName(code: string | null): string {
    if (!code) {
        return '—';
    }
    const normalized = code.toLowerCase().trim();
    return LANGUAGE_NAMES[normalized] ?? LANGUAGE_NAMES[code] ?? code;
}

type InhouseRecommendation = {
    readonly caregiver_id: string;
    readonly caregiver_name: string;
    readonly selection_priority?: number;
    readonly caregiver_tags: ReadonlyArray<string> | null;
    readonly caregiver_compliance: CaregiverComplianceStatus | null;
    readonly ehr_caregiver_status: EhrCaregiverStatus | null;
    readonly contacts: ReadonlyArray<{
        readonly type: string;
        readonly address: string;
    }>;
    readonly rank: number;
    readonly currently_available: boolean;
    readonly availability_preference?: RecommendationAvailabilityPreference | null;
    readonly schedule_availability?: RecommendationScheduleAvailability | null;
    readonly previous_visits: number | null;
    readonly contact_attempts_48h: number | null;
    readonly unavailable_until: string | null;
    readonly distance: number | null;
    readonly weekly_hours: number | null;
    readonly weekly_hours_if_assigned: number | null;
    readonly star_rating: number | null;
    readonly tags_matches: number | null;
    readonly gender: string | null;
    readonly primary_language: string | null;
    readonly supported_languages: ReadonlyArray<string> | null;
    readonly roles: ReadonlyArray<string> | null;
    readonly employment_type: string | null;
    readonly desired_weekly_hours: number | null;
    readonly is_overtime?: boolean;
    readonly overtime_triggers?: ReadonlyArray<{
        readonly rule: 'weekly_hours' | 'daily_hours' | 'consecutive_days';
        readonly projected: number;
        readonly threshold: number;
    }>;
    readonly availability_preference_overlap_minutes?: number | null;
    readonly availability_preference_overlap_ranges?: ReadonlyArray<{
        readonly start_time: string;
        readonly end_time: string;
    }> | null;
    readonly schedule_overlap_minutes?: number | null;
    readonly schedule_overlap_ranges?: ReadonlyArray<{
        readonly start_time: string;
        readonly end_time: string;
    }> | null;
    readonly covered_shift_ids?: ReadonlyArray<string>;
    readonly uncovered_shift_ids?: ReadonlyArray<string>;
    readonly uncovered_reasons_by_shift_id?: Readonly<
        Record<string, ReadonlyArray<string>>
    >;
    readonly availability_by_shift_id?: Readonly<
        Record<string, ShiftAvailabilityRead>
    >;
    readonly postal_code?: string | null;
    readonly office_name?: string | null;
    readonly is_preferred?: boolean;
    // Filters this caregiver fails while still being shown (force-included
    // rows: selected/suggested/dismissed), including `skip_list`.
    readonly filter_reasons?: ReadonlyArray<string>;
    readonly filter_reasons_by_shift_id?: Readonly<
        Record<string, ReadonlyArray<string>>
    >;
};

type InhouseRecommendationsResponse = {
    readonly recommendations?: Array<InhouseRecommendation>;
    readonly shift_id?: string;
    readonly shift_group_id?: string;
    readonly shift_duration_minutes?: number;
    readonly shift_start_time?: string;
    readonly shift_end_time?: string;
    readonly patient_tags?: ReadonlyArray<string> | null;
    readonly patient_office_name?: string | null;
    readonly shift_ids?: ReadonlyArray<string>;
    // Filters whose sole relaxation would reveal caregivers when the
    // active filters exclude everyone. Surfaced as an info-only
    // suggestion banner; the user relaxes the controls themselves.
    readonly suggested_filter_relaxations?: ReadonlyArray<string>;
};

// Human-readable labels for engine filter/coverage reason keys, used in
// coverage tooltips and "would be excluded" badges instead of raw
// snake_case strings.
const FILTER_REASON_LABELS: Record<string, string> = {
    schedule_conflict: 'Conflicting shift',
    preference_unavailable: 'Marked unavailable',
    hard_tags: 'Tag requirement',
    max_distance: 'Too far',
    office: 'Different office',
    max_contact_attempts_48h: 'Contacted recently',
    overtime: 'Would hit overtime',
    hard_unavailable_for_shift: 'Not eligible for this shift',
    skip_list: 'On skip list',
    // The API currently sends raw reason strings without occurrence polarity.
    // Negated-label rendering needs a follow-up diagnostics metadata field.
    'availability_preference:available': 'Preferred availability: available',
    'availability_preference:partial': 'Preferred availability: partial',
    'availability_preference:unavailable':
        'Preferred availability: unavailable',
    'availability_preference:unknown': 'Preferred availability: unknown',
    'schedule_availability:available': 'Scheduled availability: available',
    'schedule_availability:has_shift_conflict':
        'Scheduled availability: has shift conflict',
    'overtime_status:within_limit': 'Overtime status: within limit',
    'overtime_status:overtime': 'Overtime status: overtime',
};

function formatFilterReason(reason: string): string {
    return FILTER_REASON_LABELS[reason] ?? reason;
}

const SKIP_LIST_TOOLTIP =
    'On your skip list. They won’t be contacted. Uncheck to remove.';

function isOnSkipList(recommendation: InhouseRecommendation): boolean {
    return (
        Boolean(recommendation.filter_reasons?.includes('skip_list')) ||
        Object.values(recommendation.filter_reasons_by_shift_id ?? {}).some(
            (reasons) => reasons.includes('skip_list'),
        )
    );
}

// Skip-listed caregivers may still be unchecked, just never newly selected.
function canNewlySelectCaregiver(
    caregiverId: string,
    disabledCaregiverIds: ReadonlyArray<string>,
    skipListedCaregiverIds: ReadonlySet<string>,
): boolean {
    return (
        !disabledCaregiverIds.includes(caregiverId) &&
        !skipListedCaregiverIds.has(caregiverId)
    );
}

function SkipListBadge({ className }: { readonly className: string }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <Badge variant="destructive" className={className}>
                    On skip list
                </Badge>
            </TooltipTrigger>
            <TooltipContent>{SKIP_LIST_TOOLTIP}</TooltipContent>
        </Tooltip>
    );
}

// Filter-control names for the relaxation-suggestion banner, matching
// the filter controls the user would loosen.
const FILTER_RELAXATION_LABELS: Record<string, string> = {
    office: 'Office',
    max_contact_attempts_48h: 'Contact attempts',
    schedule_conflict: 'Scheduled availability',
    preference_unavailable: 'Preferred availability',
    max_distance: 'Distance',
    overtime: 'Overtime',
};

function formatFilterRelaxationLabel(filter: string): string {
    return (
        FILTER_RELAXATION_LABELS[filter] ??
        filter
            .split('_')
            .filter((part) => part.length > 0)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ')
    );
}

type StarRatingComparisonOperator = 'greater_than' | 'less_than';

type StarRatingComparisonFilterValue = {
    readonly operator?: StarRatingComparisonOperator;
    readonly value?: number;
};

function normalizeStarRatingBound(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }

    const parsedValue =
        typeof value === 'number'
            ? value
            : Number.parseInt(String(value).trim(), 10);

    if (!Number.isFinite(parsedValue)) {
        return undefined;
    }

    return Math.max(0, Math.min(5, Math.trunc(parsedValue)));
}

function normalizeStarRatingComparisonOperator(
    value: unknown,
): StarRatingComparisonOperator | undefined {
    if (value === 'greater_than' || value === 'less_than') {
        return value;
    }

    return undefined;
}

function normalizeStarRatingComparisonFilterValue(
    value: unknown,
): StarRatingComparisonFilterValue {
    if (!value || typeof value !== 'object') {
        return {};
    }

    const rawValue = value as {
        operator?: unknown;
        value?: unknown;
    };

    return {
        operator: normalizeStarRatingComparisonOperator(rawValue.operator),
        value: normalizeStarRatingBound(rawValue.value),
    };
}

function formatStarRating(starRating: number | null | undefined): string {
    if (starRating === null || starRating === undefined) {
        return '—';
    }

    return String(starRating);
}

function formatStarRatingComparisonFilterLabel(
    filterValue: StarRatingComparisonFilterValue,
): string | null {
    const { operator, value } =
        normalizeStarRatingComparisonFilterValue(filterValue);

    if (!operator || value === undefined) {
        return null;
    }

    return operator === 'greater_than'
        ? `Greater than ${value}`
        : `Less than ${value}`;
}

function formatStarRatingComparisonOperatorLabel(
    operator: StarRatingComparisonOperator,
): string {
    return operator === 'greater_than' ? 'greater than' : 'less than';
}

function getStarRatingComparisonFromColumnFilters(
    filters: ColumnFiltersState,
): StarRatingComparisonFilterValue {
    return normalizeStarRatingComparisonFilterValue(
        filters.find((filter) => filter.id === 'star_rating')?.value,
    );
}

function getMinStarRatingFromColumnFilters(
    filters: ColumnFiltersState,
): number | null {
    const filterValue = getStarRatingComparisonFromColumnFilters(filters);
    if (
        filterValue.operator !== 'greater_than' ||
        filterValue.value === undefined
    ) {
        return null;
    }

    return filterValue.value + 1;
}

function getMaxStarRatingFromColumnFilters(
    filters: ColumnFiltersState,
): number | null {
    const filterValue = getStarRatingComparisonFromColumnFilters(filters);
    if (
        filterValue.operator !== 'less_than' ||
        filterValue.value === undefined
    ) {
        return null;
    }

    return filterValue.value - 1;
}

export function matchesStarRatingComparisonFilter(
    starRating: number | null | undefined,
    filterValue: StarRatingComparisonFilterValue,
): boolean {
    const { operator, value } =
        normalizeStarRatingComparisonFilterValue(filterValue);

    if (!operator || value === undefined) {
        return true;
    }

    // Once a comparison is active, coordinators are explicitly filtering for
    // rated caregivers only, so unrated rows should drop out.
    if (starRating === null || starRating === undefined) {
        return false;
    }

    return operator === 'greater_than'
        ? starRating > value
        : starRating < value;
}

const starRatingComparisonFilterFn: FilterFn<InhouseRecommendation> = (
    row,
    _columnId,
    filterValue,
) =>
    matchesStarRatingComparisonFilter(
        row.original.star_rating,
        normalizeStarRatingComparisonFilterValue(filterValue),
    );

function StarRatingComparisonFilterMenu({
    filterValue,
    onApply,
    onClear,
    hideOperatorSelector = false,
    inputAriaLabel = 'Star rating value',
    lockedOperator,
    applyButtonLabel = 'Apply',
}: {
    readonly filterValue: StarRatingComparisonFilterValue;
    readonly onApply: (filterValue: StarRatingComparisonFilterValue) => void;
    readonly onClear: () => void;
    readonly hideOperatorSelector?: boolean;
    readonly inputAriaLabel?: string;
    readonly lockedOperator?: StarRatingComparisonOperator;
    readonly applyButtonLabel?: string;
}) {
    const [operator, setOperator] = useState<StarRatingComparisonOperator>(
        lockedOperator ?? filterValue.operator ?? 'greater_than',
    );
    const [valueInput, setValueInput] = useState(
        filterValue.value?.toString() ?? '',
    );

    useEffect(() => {
        setOperator(lockedOperator ?? filterValue.operator ?? 'greater_than');
        setValueInput(filterValue.value?.toString() ?? '');
    }, [filterValue.operator, filterValue.value, lockedOperator]);

    const normalizedValue = normalizeStarRatingBound(valueInput);
    const isApplyDisabled = normalizedValue === undefined;
    const appliedOperator = lockedOperator ?? operator;

    return (
        <form
            className="space-y-3 p-2"
            onSubmit={(event) => {
                event.preventDefault();
                if (normalizedValue === undefined) {
                    return;
                }
                onApply({
                    operator: appliedOperator,
                    value: normalizedValue,
                });
            }}
        >
            {!hideOperatorSelector ? (
                <div className="space-y-1">
                    <span className="text-xs font-medium">Operator</span>
                    <div className="grid grid-cols-2 gap-2">
                        <Button
                            type="button"
                            variant={
                                operator === 'greater_than'
                                    ? 'default'
                                    : 'outline'
                            }
                            size="sm"
                            onClick={() => {
                                setOperator('greater_than');
                            }}
                        >
                            Greater than
                        </Button>
                        <Button
                            type="button"
                            variant={
                                operator === 'less_than' ? 'default' : 'outline'
                            }
                            size="sm"
                            onClick={() => {
                                setOperator('less_than');
                            }}
                        >
                            Less than
                        </Button>
                    </div>
                </div>
            ) : null}
            <label className="block space-y-1 text-xs font-medium">
                <span>Rating</span>
                <Input
                    aria-label={inputAriaLabel}
                    className="h-8"
                    inputMode="numeric"
                    max="5"
                    min="0"
                    onChange={(event) => {
                        setValueInput(event.target.value);
                    }}
                    placeholder="0"
                    step="1"
                    type="number"
                    value={valueInput}
                />
            </label>
            <p className="text-muted-foreground text-xs">
                Uses a strict comparison. Unrated caregivers are excluded while
                the filter is active.
            </p>
            <div className="flex justify-end gap-2">
                <Button
                    onClick={() => {
                        setOperator('greater_than');
                        setValueInput('');
                        onClear();
                    }}
                    size="sm"
                    type="button"
                    variant="outline"
                >
                    Clear
                </Button>
                <Button disabled={isApplyDisabled} size="sm" type="submit">
                    {applyButtonLabel}
                </Button>
            </div>
        </form>
    );
}

type NormalizedColumnFilter =
    | {
          readonly id: string;
          readonly value: StarRatingComparisonFilterValue;
          readonly valueType: 'comparison';
      }
    | {
          readonly id: string;
          readonly value: MultiSelectFilterValue;
          readonly valueType: 'multi_select';
      };

type MultiSelectFilterValue = {
    readonly include: Array<string>;
    readonly exclude: Array<string>;
    readonly includeMode?: 'any' | 'all';
    readonly excludeMode?: 'any' | 'all';
};

function getAvailabilityPreference(
    recommendation: InhouseRecommendation,
): RecommendationAvailabilityPreference {
    return recommendation.availability_preference ?? 'unknown';
}

function getScheduleAvailability(
    recommendation: InhouseRecommendation,
): RecommendationScheduleAvailability {
    if (
        recommendation.schedule_availability === 'available' ||
        recommendation.schedule_availability === 'has_shift_conflict'
    ) {
        return recommendation.schedule_availability;
    }
    return (recommendation.schedule_overlap_minutes ?? 0) > 0 ||
        (recommendation.schedule_overlap_ranges?.length ?? 0) > 0
        ? 'has_shift_conflict'
        : 'available';
}

function getAvailabilityPreferenceOverlap(
    recommendation: InhouseRecommendation,
) {
    return {
        minutes: recommendation.availability_preference_overlap_minutes ?? 0,
        ranges: recommendation.availability_preference_overlap_ranges ?? [],
    };
}

function getScheduleOverlap(recommendation: InhouseRecommendation) {
    return {
        minutes: recommendation.schedule_overlap_minutes ?? 0,
        ranges: recommendation.schedule_overlap_ranges ?? [],
    };
}

function getGroupCoverageCounts(recommendation: InhouseRecommendation): {
    readonly covered: number;
    readonly total: number;
    readonly uncovered: number;
} | null {
    const covered = recommendation.covered_shift_ids?.length ?? 0;
    const uncovered = recommendation.uncovered_shift_ids?.length ?? 0;
    const total = covered + uncovered;
    if (total === 0) {
        return null;
    }
    return { covered, total, uncovered };
}

function getGroupUncoveredReasonText(
    recommendation: InhouseRecommendation,
): string | null {
    const reasonsByShift = recommendation.uncovered_reasons_by_shift_id ?? {};
    const reasons = Object.values(reasonsByShift)
        .flatMap((shiftReasons) => shiftReasons)
        .map((reason) => reason.trim())
        .filter((reason) => reason.length > 0)
        .map(formatFilterReason);
    const uniqueReasons = Array.from(new Set(reasons));
    return uniqueReasons.length > 0 ? uniqueReasons.join(', ') : null;
}

function buildInitialSelectedCaregiverIds({
    dismissedCaregiverIds,
    initialSelectedCaregiverIds,
    preSelectedCaregiverIds,
    storageKeys,
}: {
    readonly dismissedCaregiverIds: ReadonlySet<string>;
    readonly initialSelectedCaregiverIds?: ReadonlyArray<string>;
    readonly preSelectedCaregiverIds?: ReadonlyArray<string>;
    readonly storageKeys: ReadonlyArray<string>;
}): Set<string> {
    const currentSuggestions = preSelectedCaregiverIds ?? [];
    let selectedIds: Set<string>;
    if (initialSelectedCaregiverIds) {
        selectedIds = new Set(initialSelectedCaregiverIds);
    } else {
        selectedIds = new Set(currentSuggestions);
        for (const storageKey of storageKeys) {
            try {
                const stored = localStorage.getItem(storageKey);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    // New format: { suggestions, selected }
                    if (parsed?.suggestions && parsed?.selected) {
                        const suggestionsMatch =
                            parsed.suggestions.length ===
                                currentSuggestions.length &&
                            parsed.suggestions.every(
                                (id: string, i: number) =>
                                    id === currentSuggestions[i],
                            );
                        if (suggestionsMatch) {
                            selectedIds = new Set(
                                parsed.selected as Array<string>,
                            );
                            break;
                        }
                    } else if (
                        Array.isArray(parsed) &&
                        parsed.length > 0 &&
                        currentSuggestions.length === 0
                    ) {
                        // Legacy format: plain array
                        selectedIds = new Set(parsed as Array<string>);
                        break;
                    }
                }
            } catch {
                // Private browsing or corrupt data
            }
        }
    }

    for (const caregiverId of dismissedCaregiverIds) {
        selectedIds.delete(caregiverId);
    }
    return selectedIds;
}

function buildShowSelectedCaregiverIdsSnapshot({
    dismissedCaregiverIds,
    preSelectedCaregiverIds,
    selectedCaregiverIds,
}: {
    readonly dismissedCaregiverIds: ReadonlySet<string>;
    readonly preSelectedCaregiverIds?: ReadonlyArray<string>;
    readonly selectedCaregiverIds: ReadonlySet<string>;
}): Array<string> {
    // The "show selected" snapshot is the union of the agent's suggestions,
    // the dismissed caregivers (stripped from suggestions, kept visible
    // unchecked), and the live selection.
    const ids = new Set(selectedCaregiverIds);
    for (const id of preSelectedCaregiverIds ?? []) {
        ids.add(id);
    }
    for (const id of dismissedCaregiverIds) {
        ids.add(id);
    }
    return normalizeSelectedCaregiverIds(Array.from(ids));
}

function areSetsEqual<T>(left: ReadonlySet<T>, right: ReadonlySet<T>): boolean {
    if (left.size !== right.size) {
        return false;
    }
    for (const value of left) {
        if (!right.has(value)) {
            return false;
        }
    }
    return true;
}

function normalizeMultiSelectFilterValue(
    value: unknown,
): MultiSelectFilterValue {
    if (Array.isArray(value)) {
        return {
            include: [...value].map(String).sort(),
            exclude: [],
        };
    }

    if (!value || typeof value !== 'object') {
        return { include: [], exclude: [] };
    }

    const rawValue = value as {
        include?: ReadonlyArray<string>;
        exclude?: ReadonlyArray<string>;
        includeMode?: 'any' | 'all';
        excludeMode?: 'any' | 'all';
    };

    return {
        include: [...(rawValue.include ?? [])].sort(),
        exclude: [...(rawValue.exclude ?? [])].sort(),
        includeMode: rawValue.includeMode,
        excludeMode: rawValue.excludeMode,
    };
}

function normalizeColumnFilters(
    filters: ColumnFiltersState | undefined,
): Array<NormalizedColumnFilter> {
    return [...(filters ?? [])]
        .map((filter): NormalizedColumnFilter => {
            if (filter.id === STAR_RATING_COLUMN_ID) {
                return {
                    id: filter.id,
                    value: normalizeStarRatingComparisonFilterValue(
                        filter.value,
                    ),
                    valueType: 'comparison',
                };
            }

            return {
                id: filter.id,
                value: normalizeMultiSelectFilterValue(filter.value),
                valueType: 'multi_select',
            };
        })
        .filter((filter) =>
            filter.valueType === 'comparison'
                ? filter.value.operator !== undefined &&
                  filter.value.value !== undefined
                : filter.value.include.length > 0 ||
                  filter.value.exclude.length > 0,
        )
        .sort((left, right) => left.id.localeCompare(right.id));
}

function areColumnFiltersEqual(
    left: ColumnFiltersState | undefined,
    right: ColumnFiltersState | undefined,
): boolean {
    return (
        JSON.stringify(normalizeColumnFilters(left)) ===
        JSON.stringify(normalizeColumnFilters(right))
    );
}

// null = no persisted entry (seed defaults); [] = user cleared (keep empty).
function readPersistedColumnFilters(
    key: string | null,
): ColumnFiltersState | null {
    if (!key) {
        return null;
    }
    try {
        const raw = localStorage.getItem(key);
        if (raw === null) {
            return null;
        }
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as ColumnFiltersState) : null;
    } catch {
        return null;
    }
}

function writePersistedColumnFilters(
    key: string | null,
    filters: ColumnFiltersState,
): void {
    if (!key) {
        return;
    }
    try {
        localStorage.setItem(key, JSON.stringify(filters));
    } catch {
        // Ignore quota/serialization errors; persistence is best-effort.
    }
}

function removePersistedColumnFilters(key: string | null): void {
    if (!key) {
        return;
    }
    try {
        localStorage.removeItem(key);
    } catch {
        // Ignore storage errors; persistence is best-effort.
    }
}

function matchesCaregiverNameSearch(
    recommendation: InhouseRecommendation,
    normalizedQuery: string,
    formatCaregiverNameFn: (name: string) => string,
): boolean {
    if (normalizedQuery.length === 0) {
        return true;
    }
    const rawName = recommendation.caregiver_name.toLocaleLowerCase();
    const displayName = formatCaregiverNameFn(
        recommendation.caregiver_name,
    ).toLocaleLowerCase();
    return (
        rawName.includes(normalizedQuery) ||
        displayName.includes(normalizedQuery)
    );
}

function formatAvailabilityDuration(totalMinutes: number): string {
    if (totalMinutes <= 0) {
        return '0m';
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
    }
    if (hours > 0) {
        return `${hours}h`;
    }
    return `${minutes}m`;
}

type TimeRange = {
    readonly start_time: string;
    readonly end_time: string;
};

function computeAvailableRanges(
    shiftStart: string,
    shiftEnd: string,
    conflictRanges: ReadonlyArray<TimeRange>,
): Array<TimeRange> {
    const shiftStartMs = new Date(shiftStart).getTime();
    const shiftEndMs = new Date(shiftEnd).getTime();

    const sorted = [...conflictRanges]
        .map((r) => ({
            start: Math.max(new Date(r.start_time).getTime(), shiftStartMs),
            end: Math.min(new Date(r.end_time).getTime(), shiftEndMs),
        }))
        .filter((r) => r.end > r.start)
        .sort((a, b) => a.start - b.start);

    const available: Array<TimeRange> = [];
    let cursor = shiftStartMs;
    for (const conflict of sorted) {
        if (conflict.start > cursor) {
            available.push({
                start_time: new Date(cursor).toISOString(),
                end_time: new Date(conflict.start).toISOString(),
            });
        }
        cursor = Math.max(cursor, conflict.end);
    }
    if (cursor < shiftEndMs) {
        available.push({
            start_time: new Date(cursor).toISOString(),
            end_time: new Date(shiftEndMs).toISOString(),
        });
    }
    return available;
}

function formatOverlapRanges(
    ranges: ReadonlyArray<TimeRange>,
    timezone: string,
): string {
    return ranges
        .map((range) => {
            return `${formatTime(range.start_time, timezone)} - ${formatTime(
                range.end_time,
                timezone,
            )}`;
        })
        .join(', ');
}

type SuggestionFeedback = 'up' | 'down';

type PersistedSuggestionFeedback = {
    readonly is_helpful: boolean;
    readonly feedback: string | null;
    readonly caregiver_name: string | null;
    readonly updated_at: string;
};

const SUGGESTION_FEEDBACK_UNDO_TOKEN = '__UNDO__';

function getSuggestedBadgeClassName(
    feedback: SuggestionFeedback | null,
    isInteractive: boolean,
): string {
    return cn(
        isInteractive ? 'cursor-pointer' : 'cursor-default',
        'gap-1 rounded-full px-2.5 pb-[3px] pt-[2px] text-[11px] font-medium',
        feedback === 'up'
            ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/50 dark:text-green-400'
            : feedback === 'down'
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-400'
              : 'border-transparent bg-[#5533EE] text-white',
    );
}

function SuggestedReasoningBadge({
    caregiverId,
    reasoning,
    feedback: feedbackProp,
    onFeedback,
    onUndoFeedback,
    persistedFeedback,
    organizationId,
}: {
    readonly caregiverId: string;
    readonly reasoning: string;
    readonly feedback: SuggestionFeedback | null;
    readonly onFeedback: (
        caregiverId: string,
        feedback: SuggestionFeedback,
        comment?: string,
    ) => Promise<boolean>;
    readonly onUndoFeedback?: (caregiverId: string) => void;
    readonly persistedFeedback?: PersistedSuggestionFeedback | null;
    readonly organizationId?: string;
}) {
    const [localFeedback, setLocalFeedback] =
        useState<SuggestionFeedback | null>(feedbackProp);
    useEffect(() => {
        setLocalFeedback(feedbackProp);
    }, [feedbackProp]);
    const feedback = localFeedback;
    // The retired per-model fork flag that gated this text was never
    // enabled in production, so the reasoning stays hidden until a
    // deliberate product decision shows it (likely via
    // outreach_agent_swarm_mode_enabled).
    const showReasoningText = false;
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [showCommentInput, setShowCommentInput] = useState(false);
    const [comment, setComment] = useState('');
    const posthog = usePostHog();
    const mode = useMode();
    const draftIncludesVoiceInputRef = useRef(false);

    const {
        canRecordAudio,
        isRecording,
        isTranscribing,
        voiceInputError,
        startRecording,
        stopRecording,
    } = useVoiceInput({
        organizationId: organizationId ?? '',
        currentText: comment,
        onTranscribed: (appendedText) => {
            draftIncludesVoiceInputRef.current = true;
            setComment(appendedText);
        },
        composer: 'suggestion_feedback',
        voiceInputFlow: 'outreach_suggestion_feedback',
        disabled: !organizationId || !showCommentInput,
    });

    useEffect(() => {
        if (!showCommentInput) {
            draftIncludesVoiceInputRef.current = false;
        }
    }, [showCommentInput]);

    const showMicButton =
        !!organizationId && canRecordAudio && showCommentInput;
    const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const HOVER_OPEN_DELAY_MS = 343;
    const HOVER_CLOSE_DELAY_MS = 120;

    const clearOpenTimer = useCallback(() => {
        if (openTimerRef.current !== null) {
            clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
    }, []);

    const clearCloseTimer = useCallback(() => {
        if (closeTimerRef.current !== null) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);

    const openPopover = useCallback(() => {
        clearOpenTimer();
        clearCloseTimer();
        setIsPopoverOpen(true);
    }, [clearOpenTimer, clearCloseTimer]);

    const scheduleOpen = useCallback(() => {
        clearOpenTimer();
        clearCloseTimer();
        if (isPopoverOpen) {
            return;
        }
        openTimerRef.current = setTimeout(() => {
            setIsPopoverOpen(true);
            openTimerRef.current = null;
        }, HOVER_OPEN_DELAY_MS);
    }, [clearOpenTimer, clearCloseTimer, isPopoverOpen]);

    const scheduleClose = useCallback(() => {
        clearOpenTimer();
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
            setIsPopoverOpen(false);
        }, HOVER_CLOSE_DELAY_MS);
    }, [clearOpenTimer, clearCloseTimer]);

    useEffect(() => {
        return () => {
            clearOpenTimer();
            clearCloseTimer();
        };
    }, [clearOpenTimer, clearCloseTimer]);

    const badgeClassName = getSuggestedBadgeClassName(feedback, true);

    return (
        <Popover
            open={isPopoverOpen}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    clearOpenTimer();
                    clearCloseTimer();
                    setIsPopoverOpen(false);
                }
            }}
        >
            <PopoverAnchor asChild={true}>
                <span
                    onMouseEnter={scheduleOpen}
                    onMouseLeave={showCommentInput ? undefined : scheduleClose}
                    className="inline-flex"
                    data-row-click-ignore={true}
                >
                    <Badge className={badgeClassName}>Suggested</Badge>
                </span>
            </PopoverAnchor>
            <PopoverContent
                align="start"
                className={
                    showReasoningText || showCommentInput
                        ? 'w-[calc(100vw-2rem)] max-w-[380px] p-3'
                        : 'w-auto p-3'
                }
                onClick={(event) => {
                    event.stopPropagation();
                }}
                onMouseEnter={openPopover}
                onMouseLeave={showCommentInput ? undefined : scheduleClose}
                side="top"
            >
                <div className="space-y-3">
                    {showReasoningText ? (
                        <Fragment>
                            <p className="text-sm font-medium">
                                Why Phoebe suggested this caregiver
                            </p>
                            <Markdown className="text-foreground [&_*]:text-foreground max-h-[220px] overflow-y-auto text-sm [&_p]:m-0 [&_ul]:my-0">
                                {reasoning}
                            </Markdown>
                        </Fragment>
                    ) : null}
                    <div className="flex gap-2">
                        <Button
                            className="gap-1.5"
                            onClick={(event) => {
                                event.stopPropagation();
                                setShowCommentInput(false);
                                setComment('');
                                setLocalFeedback('up');
                                void onFeedback(caregiverId, 'up');
                            }}
                            size="sm"
                            type="button"
                            variant={feedback === 'up' ? 'default' : 'outline'}
                        >
                            <ThumbsUp className="h-3.5 w-3.5" />
                            Strong Fit
                        </Button>
                        <Button
                            className="gap-1.5"
                            onClick={(event) => {
                                event.stopPropagation();
                                setLocalFeedback('down');
                                setShowCommentInput(true);
                            }}
                            size="sm"
                            type="button"
                            variant={
                                feedback === 'down' ? 'default' : 'outline'
                            }
                        >
                            <ThumbsDown className="h-3.5 w-3.5" />
                            Weak Fit
                        </Button>
                        {feedback !== null && onUndoFeedback ? (
                            <Button
                                className="ml-auto"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setShowCommentInput(false);
                                    setComment('');
                                    setLocalFeedback(null);
                                    onUndoFeedback(caregiverId);
                                }}
                                size="sm"
                                type="button"
                                variant="ghost"
                            >
                                Undo
                            </Button>
                        ) : null}
                    </div>
                    {showCommentInput && (
                        <div className="space-y-2">
                            <div
                                className="border-input rounded-lg border bg-transparent px-3 py-2"
                                onClick={(event) => {
                                    event.stopPropagation();
                                }}
                            >
                                <div className="flex items-start gap-2">
                                    <Textarea
                                        className="max-h-32 min-h-[60px] flex-1 resize-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0"
                                        onChange={(event) => {
                                            setComment(event.target.value);
                                        }}
                                        placeholder={
                                            isRecording
                                                ? 'Listening\u2026'
                                                : 'What was wrong with this suggestion?'
                                        }
                                        value={comment}
                                        disabled={isRecording || isTranscribing}
                                    />
                                    <div className="flex flex-col gap-1 self-end">
                                        {showMicButton ? (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size={
                                                    isTranscribing
                                                        ? 'sm'
                                                        : 'icon'
                                                }
                                                className={
                                                    isTranscribing
                                                        ? 'h-7 flex-shrink-0 rounded-full px-2 text-xs font-medium'
                                                        : 'h-7 w-7 flex-shrink-0 rounded-full'
                                                }
                                                aria-label={
                                                    isTranscribing
                                                        ? 'Transcribing voice message'
                                                        : isRecording
                                                          ? 'Stop recording'
                                                          : 'Record voice message'
                                                }
                                                disabled={isTranscribing}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    if (isRecording) {
                                                        stopRecording();
                                                        return;
                                                    }

                                                    void startRecording();
                                                }}
                                            >
                                                {isTranscribing ? (
                                                    <span className="flex items-center gap-1">
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                        <span>
                                                            Transcribing
                                                        </span>
                                                    </span>
                                                ) : (
                                                    <span className="relative flex items-center justify-center">
                                                        <Mic className="h-3 w-3" />
                                                        {isRecording ? (
                                                            <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                                                        ) : null}
                                                    </span>
                                                )}
                                            </Button>
                                        ) : null}
                                        <Button
                                            type="button"
                                            size="icon"
                                            className="h-7 w-7 flex-shrink-0 rounded-full"
                                            aria-label="Submit feedback"
                                            disabled={
                                                isRecording || isTranscribing
                                            }
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                const includedVoiceInput =
                                                    draftIncludesVoiceInputRef.current;
                                                const trimmedComment =
                                                    comment.trim();
                                                setShowCommentInput(false);
                                                setComment('');
                                                draftIncludesVoiceInputRef.current = false;
                                                void onFeedback(
                                                    caregiverId,
                                                    'down',
                                                    trimmedComment || undefined,
                                                ).then((succeeded) => {
                                                    if (
                                                        succeeded &&
                                                        includedVoiceInput &&
                                                        trimmedComment &&
                                                        organizationId
                                                    ) {
                                                        posthog?.capture(
                                                            EVENTS.VOICE_INPUT_FEEDBACK_SUBMITTED,
                                                            {
                                                                composer:
                                                                    'suggestion_feedback',
                                                                draft_length:
                                                                    trimmedComment.length,
                                                                mode,
                                                                organization_id:
                                                                    organizationId,
                                                                voice_input_flow:
                                                                    'outreach_suggestion_feedback',
                                                            },
                                                        );
                                                    }
                                                });
                                            }}
                                        >
                                            <ArrowUp className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            {voiceInputError ? (
                                <p className="text-destructive text-xs">
                                    {voiceInputError}
                                </p>
                            ) : null}
                        </div>
                    )}
                    {persistedFeedback && (
                        <div className="text-muted-foreground border-t pt-2 text-xs">
                            <p>
                                Rated{' '}
                                {persistedFeedback.is_helpful
                                    ? 'strong fit'
                                    : 'weak fit'}
                            </p>
                            {persistedFeedback.feedback ? (
                                <p className="mt-1 italic">
                                    {'\u201c'}
                                    {persistedFeedback.feedback}
                                    {'\u201d'}
                                </p>
                            ) : null}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

const TAGS_HOVER_OPEN_DELAY_MS = 500;
const TAGS_HOVER_CLOSE_DELAY_MS = 400;

function TagsHoverCell({
    tags,
    patientTags,
}: {
    readonly tags: ReadonlyArray<string>;
    readonly patientTags?: ReadonlyArray<string>;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearOpenTimer = useCallback(() => {
        if (openTimerRef.current !== null) {
            clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
    }, []);

    const clearCloseTimer = useCallback(() => {
        if (closeTimerRef.current !== null) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }, []);

    const openPopover = useCallback(() => {
        clearOpenTimer();
        clearCloseTimer();
        setIsOpen(true);
    }, [clearOpenTimer, clearCloseTimer]);

    const scheduleOpen = useCallback(() => {
        clearOpenTimer();
        clearCloseTimer();
        if (isOpen) {
            return;
        }
        openTimerRef.current = setTimeout(() => {
            setIsOpen(true);
            openTimerRef.current = null;
        }, TAGS_HOVER_OPEN_DELAY_MS);
    }, [clearOpenTimer, clearCloseTimer, isOpen]);

    const scheduleClose = useCallback(() => {
        clearOpenTimer();
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
            setIsOpen(false);
        }, TAGS_HOVER_CLOSE_DELAY_MS);
    }, [clearOpenTimer, clearCloseTimer]);

    useEffect(() => {
        return () => {
            clearOpenTimer();
            clearCloseTimer();
        };
    }, [clearOpenTimer, clearCloseTimer]);

    const patientTagSet = useMemo(
        () => new Set(patientTags?.map((t) => t.toLowerCase()) ?? []),
        [patientTags],
    );

    if (tags.length === 0) {
        return <span className="text-foreground">—</span>;
    }

    const maxVisible = 2;
    const sorted = [...tags].sort((a, b) => {
        const aMatch = patientTagSet.has(a.toLowerCase());
        const bMatch = patientTagSet.has(b.toLowerCase());
        if (aMatch !== bMatch) {
            return aMatch ? -1 : 1;
        }
        return 0;
    });
    const visibleTags = sorted.slice(0, maxVisible);
    const hiddenTags = sorted.slice(maxVisible);

    const renderBadge = (t: string) => {
        const isMatch = patientTagSet.has(t.toLowerCase());
        const badge = (
            <Badge
                key={t}
                className={
                    isMatch
                        ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950/50 dark:text-green-400'
                        : 'text-foreground dark:bg-muted border-transparent bg-[#F0F5F5]'
                }
            >
                {t}
            </Badge>
        );
        if (isMatch) {
            return (
                <Tooltip key={t}>
                    <TooltipTrigger asChild={true}>{badge}</TooltipTrigger>
                    <TooltipContent>Matches patient tag</TooltipContent>
                </Tooltip>
            );
        }
        return badge;
    };

    return (
        <Popover
            open={isOpen}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    clearOpenTimer();
                    clearCloseTimer();
                    setIsOpen(false);
                }
            }}
        >
            <PopoverAnchor asChild={true}>
                <div
                    className="flex flex-wrap items-center gap-1"
                    onMouseEnter={scheduleOpen}
                    onMouseLeave={scheduleClose}
                >
                    {visibleTags.map(renderBadge)}
                    {hiddenTags.length > 0 && (
                        <span className="text-muted-foreground cursor-default text-xs">
                            +{hiddenTags.length}
                        </span>
                    )}
                </div>
            </PopoverAnchor>
            <PopoverContent
                align="start"
                side="bottom"
                className="bg-background max-h-48 max-w-64 overflow-x-hidden overflow-y-auto border p-2 shadow-md"
                onMouseEnter={openPopover}
                onMouseLeave={scheduleClose}
            >
                <div className="flex flex-wrap gap-1">
                    {sorted.map(renderBadge)}
                </div>
            </PopoverContent>
        </Popover>
    );
}

// Shared stable reference for the common case where no caregivers are
// disabled. A fresh `[]` default would change identity every render, which
// rebuilds the memoized `columns` and forces TanStack to remount every header
// and cell component (including the Radix Checkbox in the select header) on
// each render.
const EMPTY_DISABLED_CAREGIVER_IDS: Array<string> = [];
const NO_SHIFT_IDS: ReadonlySet<string> = new Set();
const SELECTED_FIRST_SORTING: SortingState = [
    { id: 'selection_priority', desc: true },
];
const INITIAL_RECOMMENDATION_SORTING: SortingState = [
    { id: 'previous_visits', desc: true },
    { id: 'schedule_availability', desc: false },
];

/**
 * Which shifts of an ANY-mode group each caregiver is currently staged for.
 * Distinct from the `group_coverage` column, which reports which shifts a
 * caregiver is *eligible* for.
 */
type OutreachSelectionCoverage = {
    readonly shiftIds: ReadonlyArray<string>;
    readonly shiftLabelById: ReadonlyMap<string, string>;
    readonly stagedShiftIdsByCaregiverId: ReadonlyMap<
        string,
        ReadonlySet<string>
    >;
    /** Picks the caregiver for every shift of the group in one write. */
    readonly onSelectEveryShift: (caregiverId: string) => void;
};

function countCoveredSelectionShifts(
    coverage: OutreachSelectionCoverage,
    recommendation: InhouseRecommendation,
) {
    const stagedShiftIds = coverage.stagedShiftIdsByCaregiverId.get(
        recommendation.caregiver_id,
    );
    const coveredShiftIds = coverage.shiftIds.filter((shiftId) =>
        Boolean(stagedShiftIds?.has(shiftId)),
    );
    return {
        covered: coveredShiftIds.length,
        coveredLabels: coveredShiftIds.flatMap((shiftId) => {
            const label = coverage.shiftLabelById.get(shiftId);
            return label ? [label] : [];
        }),
        total: coverage.shiftIds.length,
    };
}

const createInhouseRecommendationColumns = (
    disabledCaregiverIds: Array<string>,
    skipListedCaregiverIds: ReadonlySet<string>,
    selectedCaregiverIds: ReadonlySet<string>,
    setSelectedCaregiverIds: (
        ids: Set<string> | ((prev: Set<string>) => Set<string>),
    ) => void,
    allEnabledCaregiverIds: Array<string>,
    showGroupCoverage: boolean,
    formatCaregiverNameFn: (name: string) => string,
    sortByLastName: boolean,
    timezone: string,
    overtimeHoursLimit: number | null,
    shiftDurationMinutes: number | null,
    shiftStartTime: string | null,
    shiftEndTime: string | null,
    onCheckboxClick?: (caregiverId: string, shiftKey: boolean) => void,
    agentReasoningMap?: ReadonlyMap<string, string>,
    suggestedCaregiverIdSet?: ReadonlySet<string>,
    suggestionFeedbackByCaregiverId?: Readonly<
        Record<string, SuggestionFeedback>
    >,
    onSuggestionFeedback?: (
        caregiverId: string,
        feedback: SuggestionFeedback,
        comment?: string,
    ) => Promise<boolean>,
    onSuggestionFeedbackUndo?: (caregiverId: string) => void,
    persistedFeedbackByCaregiver?: Readonly<
        Record<string, PersistedSuggestionFeedback>
    >,
    onCaregiverClick?: (caregiverId: string) => void,
    patientTags?: ReadonlyArray<string>,
    organizationId?: string,
    usesV2AvailabilityPreferenceStatus = false,
    selectionDisabled = false,
    selectionCoverage?: OutreachSelectionCoverage,
    uiV3 = false,
): Array<ColumnDef<InhouseRecommendation>> => {
    const getIsOvertime = (recommendation: InhouseRecommendation): boolean => {
        if (recommendation.is_overtime === true) {
            return true;
        }

        if (
            overtimeHoursLimit === null ||
            recommendation.weekly_hours_if_assigned === null
        ) {
            return false;
        }

        return recommendation.weekly_hours_if_assigned > overtimeHoursLimit;
    };

    const formatTrigger = (trigger: {
        readonly rule: 'weekly_hours' | 'daily_hours' | 'consecutive_days';
        readonly projected: number;
        readonly threshold: number;
    }): string => {
        switch (trigger.rule) {
            case 'weekly_hours':
                return `Overtime: ${trigger.projected.toFixed(1)}/${trigger.threshold.toFixed(1)} hrs/week`;
            case 'daily_hours':
                return `Overtime: ${trigger.projected.toFixed(1)}/${trigger.threshold.toFixed(1)} hrs/day`;
            case 'consecutive_days':
                return `Overtime: ${Math.round(trigger.projected)}/${Math.round(trigger.threshold)} days in a row`;
            default: {
                const _exhaustive: never = trigger.rule;
                return _exhaustive;
            }
        }
    };

    return [
        {
            id: 'select',
            size: 50,
            header: ({ table }) => {
                const currentPageRows = table.getRowModel().rows;
                const currentPageEnabledIds = currentPageRows
                    .filter((row) =>
                        canNewlySelectCaregiver(
                            row.original.caregiver_id,
                            disabledCaregiverIds,
                            skipListedCaregiverIds,
                        ),
                    )
                    .map((row) => row.original.caregiver_id);

                const currentPageSelectedCount = currentPageEnabledIds.filter(
                    (id) => selectedCaregiverIds.has(id),
                ).length;
                const currentPageEnabled = currentPageEnabledIds.length;
                const allOnPageSelected =
                    currentPageEnabled > 0 &&
                    currentPageSelectedCount === currentPageEnabled;

                let checkboxState: boolean | 'indeterminate' = false;
                if (allOnPageSelected) {
                    checkboxState = true;
                } else if (currentPageSelectedCount > 0) {
                    checkboxState = 'indeterminate';
                }

                const isSinglePage =
                    currentPageEnabled === allEnabledCaregiverIds.length;
                const nextActionLabel = allOnPageSelected
                    ? 'Clear'
                    : isSinglePage
                      ? 'All'
                      : 'Page';

                return (
                    <div className="flex flex-col items-center justify-center gap-0.5">
                        <Checkbox
                            checked={checkboxState}
                            disabled={selectionDisabled}
                            onCheckedChange={() => {
                                if (selectionDisabled) {
                                    return;
                                }
                                setSelectedCaregiverIds((prev) => {
                                    const newSet = new Set(prev);
                                    if (allOnPageSelected) {
                                        currentPageEnabledIds.forEach((id) =>
                                            newSet.delete(id),
                                        );
                                    } else {
                                        currentPageEnabledIds.forEach((id) =>
                                            newSet.add(id),
                                        );
                                    }
                                    return newSet;
                                });
                            }}
                            aria-label="Toggle page selection"
                            className="cursor-pointer"
                            title={
                                selectionDisabled
                                    ? 'Generating recommendations'
                                    : allOnPageSelected
                                      ? 'Clear page'
                                      : 'Select page'
                            }
                        />
                        {/* v3 drops the caption; the checkbox's tooltip
                            already says what a click does. */}
                        {uiV3 ? null : (
                            <span className="text-muted-foreground w-[25px] text-center text-[10px] whitespace-nowrap">
                                {nextActionLabel}
                            </span>
                        )}
                    </div>
                );
            },
            cell: ({ row }) => {
                const isSelected = selectedCaregiverIds.has(
                    row.original.caregiver_id,
                );
                const canSelect = canNewlySelectCaregiver(
                    row.original.caregiver_id,
                    disabledCaregiverIds,
                    skipListedCaregiverIds,
                );
                const isSkipListed = skipListedCaregiverIds.has(
                    row.original.caregiver_id,
                );
                const isDisabled =
                    disabledCaregiverIds.includes(row.original.caregiver_id) ||
                    selectionDisabled ||
                    (!isSelected && !canSelect);
                // v3 lets a coordinator pick single shifts of an any-shift
                // group, so a caregiver can be picked for only some of them.
                const pickedShifts =
                    uiV3 && selectionCoverage
                        ? countCoveredSelectionShifts(
                              selectionCoverage,
                              row.original,
                          )
                        : null;
                const isPickedForSomeShifts =
                    pickedShifts !== null &&
                    pickedShifts.covered > 0 &&
                    pickedShifts.covered < pickedShifts.total;
                return (
                    <div className="flex items-center justify-center">
                        <Checkbox
                            checked={
                                isPickedForSomeShifts
                                    ? 'indeterminate'
                                    : isSelected
                            }
                            disabled={isDisabled}
                            onCheckedChange={(value) => {
                                if (value === true && !canSelect) {
                                    return;
                                }
                                if (isPickedForSomeShifts) {
                                    // Already in the selected set, so the
                                    // set update below would be a no-op.
                                    selectionCoverage?.onSelectEveryShift(
                                        row.original.caregiver_id,
                                    );
                                    return;
                                }
                                setSelectedCaregiverIds((prev) => {
                                    const newSet = new Set(prev);
                                    if (value) {
                                        newSet.add(row.original.caregiver_id);
                                    } else {
                                        newSet.delete(
                                            row.original.caregiver_id,
                                        );
                                    }
                                    return newSet;
                                });
                            }}
                            aria-label="Select row"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isDisabled && onCheckboxClick) {
                                    onCheckboxClick(
                                        row.original.caregiver_id,
                                        e.shiftKey,
                                    );
                                }
                            }}
                            className={
                                isDisabled
                                    ? 'cursor-not-allowed'
                                    : 'cursor-pointer'
                            }
                            title={
                                selectionDisabled
                                    ? 'Generating recommendations'
                                    : isSkipListed && !isSelected
                                      ? 'On your skip list'
                                      : isDisabled
                                        ? 'Already contacted'
                                        : isSelected
                                          ? 'Remove from selection'
                                          : 'Select caregiver (Shift+click to select range)'
                            }
                        />
                    </div>
                );
            },
            enableSorting: false,
            enableHiding: false,
            meta: { pinnedLeft: true },
        },
        {
            id: 'selection_priority',
            accessorFn: (row: InhouseRecommendation) =>
                row.selection_priority ?? 0,
            enableSorting: true,
            enableHiding: false,
            meta: { pinnedLeft: true },
        },
        ...(showGroupCoverage
            ? [
                  {
                      id: 'group_coverage',
                      accessorFn: (row: InhouseRecommendation) =>
                          getGroupCoverageCounts(row)?.covered ?? 0,
                      header: ({ column }) => (
                          <DataTableColumnHeader
                              column={column}
                              title="Coverage"
                          />
                      ),
                      enableSorting: true,
                      enableHiding: false,
                      meta: { pinnedLeft: true },
                      cell: ({ row }) => {
                          const coverage = getGroupCoverageCounts(row.original);
                          if (!coverage) {
                              return (
                                  <span className="text-foreground text-sm">
                                      —
                                  </span>
                              );
                          }
                          const label = `Covers ${coverage.covered}/${coverage.total}`;
                          const reasonText = getGroupUncoveredReasonText(
                              row.original,
                          );
                          const isFullCoverage = coverage.uncovered === 0;
                          return (
                              <Tooltip>
                                  <TooltipTrigger asChild={true}>
                                      <span
                                          className={cn(
                                              'inline-flex items-center gap-1 text-sm font-medium',
                                              isFullCoverage
                                                  ? 'text-green-700 dark:text-green-400'
                                                  : 'text-amber-700 dark:text-amber-400',
                                          )}
                                      >
                                          {isFullCoverage ? (
                                              <Check className="h-3.5 w-3.5" />
                                          ) : (
                                              <AlertTriangle className="h-3.5 w-3.5" />
                                          )}
                                          {uiV3
                                              ? `${coverage.covered}/${coverage.total}`
                                              : label}
                                      </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                      {reasonText ?? label}
                                  </TooltipContent>
                              </Tooltip>
                          );
                      },
                  } satisfies ColumnDef<InhouseRecommendation>,
              ]
            : []),
        ...(selectionCoverage
            ? [
                  {
                      id: 'selection_coverage',
                      accessorFn: (row: InhouseRecommendation) =>
                          countCoveredSelectionShifts(selectionCoverage, row)
                              .covered,
                      header: ({ column }) => (
                          <DataTableColumnHeader
                              column={column}
                              title="Selected shifts"
                          />
                      ),
                      enableSorting: true,
                      enableHiding: false,
                      meta: { pinnedLeft: true },
                      cell: ({ row }) => {
                          const { covered, coveredLabels, total } =
                              countCoveredSelectionShifts(
                                  selectionCoverage,
                                  row.original,
                              );
                          return (
                              <Tooltip>
                                  <TooltipTrigger asChild={true}>
                                      <span
                                          className={cn(
                                              'text-sm font-medium',
                                              covered === 0
                                                  ? 'text-muted-foreground'
                                                  : 'text-foreground',
                                          )}
                                      >
                                          {covered}/{total}
                                      </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                      {covered === 0
                                          ? 'Not selected for any shift in this group'
                                          : coveredLabels.length > 0
                                            ? `Selected for ${coveredLabels.join(', ')}`
                                            : `Selected for ${covered} of ${total} shifts`}
                                  </TooltipContent>
                              </Tooltip>
                          );
                      },
                  } satisfies ColumnDef<InhouseRecommendation>,
              ]
            : []),
        {
            id: 'caregiver_name',
            accessorKey: 'caregiver_name',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Name" />
            ),
            enableSorting: true,
            enableHiding: false,
            meta: { pinnedLeft: true },
            sortFn: (rowA, rowB, columnId) => {
                const nameA = rowA.getValue<string>(columnId) ?? '';
                const nameB = rowB.getValue<string>(columnId) ?? '';
                return compareCaregiverDisplayName(
                    nameA,
                    nameB,
                    formatCaregiverNameFn,
                    sortByLastName,
                );
            },
            cell: ({ row }) => {
                const name = row.getValue('caregiver_name') as string;
                const displayName = formatCaregiverNameFn(name);
                const isDisabled = disabledCaregiverIds.includes(
                    row.original.caregiver_id,
                );
                const isAvailable = row.original.currently_available;
                const unavailableUntil = row.original.unavailable_until;
                const reasoning = agentReasoningMap?.get(
                    row.original.caregiver_id,
                );
                const suggestionFeedback =
                    suggestionFeedbackByCaregiverId?.[
                        row.original.caregiver_id
                    ] ?? null;
                const hasInteractiveSuggestionBadge = Boolean(
                    reasoning && onSuggestionFeedback,
                );
                const showPlainSuggestedBadge =
                    !isDisabled &&
                    (suggestedCaregiverIdSet?.has(row.original.caregiver_id) ??
                        false) &&
                    !hasInteractiveSuggestionBadge;

                return (
                    <div className="flex items-center gap-2">
                        {onCaregiverClick ? (
                            <Tooltip>
                                <TooltipTrigger asChild={true}>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCaregiverClick(
                                                row.original.caregiver_id,
                                            );
                                        }}
                                        className={cn(
                                            'w-[140px] shrink-0 cursor-pointer truncate text-left font-medium',
                                            uiV3
                                                ? 'text-foreground hover:underline'
                                                : 'text-muted-foreground underline',
                                        )}
                                    >
                                        {displayName}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>{displayName}</TooltipContent>
                            </Tooltip>
                        ) : (
                            <Tooltip>
                                <TooltipTrigger asChild={true}>
                                    <span className="w-[140px] shrink-0 truncate font-medium">
                                        {displayName}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>{displayName}</TooltipContent>
                            </Tooltip>
                        )}
                        {isDisabled ? (
                            <Badge
                                variant="secondary"
                                className="shrink-0 text-xs"
                            >
                                Already in outreach
                            </Badge>
                        ) : null}
                        {showPlainSuggestedBadge ? (
                            <Badge
                                className={getSuggestedBadgeClassName(
                                    null,
                                    false,
                                )}
                            >
                                Suggested
                            </Badge>
                        ) : null}
                        {reasoning && onSuggestionFeedback && (
                            <SuggestedReasoningBadge
                                caregiverId={row.original.caregiver_id}
                                feedback={suggestionFeedback}
                                onFeedback={onSuggestionFeedback}
                                onUndoFeedback={onSuggestionFeedbackUndo}
                                organizationId={organizationId}
                                persistedFeedback={
                                    persistedFeedbackByCaregiver?.[
                                        row.original.caregiver_id
                                    ] ?? null
                                }
                                reasoning={reasoning}
                            />
                        )}
                        {!isAvailable && unavailableUntil && (
                            <Tooltip>
                                <TooltipTrigger asChild={true}>
                                    <Badge
                                        variant="secondary"
                                        className="cursor-help text-xs"
                                    >
                                        On Shift Now
                                    </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                    Busy right now. Available at{' '}
                                    {formatTime(unavailableUntil, timezone)}.
                                </TooltipContent>
                            </Tooltip>
                        )}
                        {skipListedCaregiverIds.has(
                            row.original.caregiver_id,
                        ) ? (
                            <SkipListBadge className="cursor-help text-xs" />
                        ) : (
                            (row.original.filter_reasons?.length ?? 0) > 0 && (
                                <Tooltip>
                                    <TooltipTrigger asChild={true}>
                                        <Badge
                                            variant="secondary"
                                            className="cursor-help text-xs text-amber-700 dark:text-amber-400"
                                        >
                                            Outside filters
                                        </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Shown because it’s selected or
                                        suggested. Would be excluded:{' '}
                                        {(row.original.filter_reasons ?? [])
                                            .map(formatFilterReason)
                                            .join(', ')}
                                    </TooltipContent>
                                </Tooltip>
                            )
                        )}
                    </div>
                );
            },
        },
        {
            id: 'ehr_caregiver_status',
            accessorKey: 'ehr_caregiver_status',
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Caregiver status"
                />
            ),
            enableSorting: true,
            cell: ({ row }) =>
                row.original.ehr_caregiver_status ? (
                    <EhrCaregiverStatusBadge
                        status={row.original.ehr_caregiver_status}
                    />
                ) : (
                    <span className="text-foreground">—</span>
                ),
        },
        {
            id: 'caregiver_compliance',
            accessorFn: (row) => row.caregiver_compliance?.state ?? '',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Compliance" />
            ),
            enableSorting: true,
            cell: ({ row }) =>
                row.original.caregiver_compliance ? (
                    <CaregiverComplianceBadge
                        compliance={row.original.caregiver_compliance}
                    />
                ) : (
                    <span className="text-foreground">—</span>
                ),
        },
        {
            id: 'availability_preference',
            accessorFn: (row) => {
                const pref = getAvailabilityPreference(row);
                if (usesV2AvailabilityPreferenceStatus) {
                    return pref;
                }
                if (pref !== 'available') {
                    return pref;
                }
                const { minutes, ranges } =
                    getAvailabilityPreferenceOverlap(row);
                if (
                    minutes > 0 &&
                    ranges.length > 0 &&
                    shiftDurationMinutes !== null &&
                    shiftDurationMinutes > 0 &&
                    minutes < shiftDurationMinutes
                ) {
                    return 'partial_conflict';
                }
                return 'available';
            },
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Preferred availability"
                    tooltip={
                        'Based on the caregiver\u2019s saved availability preferences'
                    }
                />
            ),
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: 'inMultiSelect',
            cell: ({ row }) => {
                const preference = getAvailabilityPreference(row.original);
                const { minutes: overlapMinutes, ranges: overlapRanges } =
                    getAvailabilityPreferenceOverlap(row.original);
                const hasOverlap =
                    overlapMinutes > 0 && overlapRanges.length > 0;
                const isFullShift =
                    hasOverlap &&
                    shiftDurationMinutes !== null &&
                    shiftDurationMinutes > 0 &&
                    overlapMinutes >= shiftDurationMinutes;

                const shiftDuration =
                    shiftDurationMinutes !== null && shiftDurationMinutes > 0
                        ? formatAvailabilityDuration(shiftDurationMinutes)
                        : null;
                const detailText = hasOverlap
                    ? `${formatAvailabilityDuration(overlapMinutes)}${shiftDuration ? `/${shiftDuration}` : ''} available · ${formatOverlapRanges(overlapRanges, timezone)}`
                    : null;

                if (usesV2AvailabilityPreferenceStatus) {
                    if (preference === 'partial') {
                        const partialDetailText =
                            detailText ??
                            'Partially available in saved availability preferences';
                        return (
                            <div className="flex max-w-[14rem] flex-col">
                                <span className="text-sm font-medium text-amber-600">
                                    Partial availability
                                </span>
                                <Tooltip>
                                    <TooltipTrigger asChild={true}>
                                        <span className="text-muted-foreground truncate text-xs">
                                            {partialDetailText}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {partialDetailText}
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                        );
                    }
                } else {
                    if (isFullShift) {
                        return (
                            <Tooltip>
                                <TooltipTrigger asChild={true}>
                                    <Check className="h-4 w-4 text-green-600" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    Available for the entire shift
                                </TooltipContent>
                            </Tooltip>
                        );
                    }
                    if (hasOverlap) {
                        const legacyDetailText =
                            detailText ??
                            'Partially available in saved availability preferences';
                        return (
                            <div className="flex max-w-[14rem] flex-col">
                                <span className="text-sm font-medium text-amber-600">
                                    Partial availability conflict
                                </span>
                                <Tooltip>
                                    <TooltipTrigger asChild={true}>
                                        <span className="text-muted-foreground truncate text-xs">
                                            {legacyDetailText}
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {legacyDetailText}
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                        );
                    }
                }
                if (preference === 'available') {
                    return (
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <Check className="h-4 w-4 text-green-600" />
                            </TooltipTrigger>
                            <TooltipContent>
                                No availability preference conflicts
                            </TooltipContent>
                        </Tooltip>
                    );
                }
                if (preference === 'unavailable') {
                    return (
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <span className="text-sm font-medium text-red-600">
                                    Preference conflict
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                Marked unavailable in saved availability
                                preferences
                            </TooltipContent>
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip>
                        <TooltipTrigger asChild={true}>
                            <span className="text-foreground">—</span>
                        </TooltipTrigger>
                        <TooltipContent>
                            No availability preference info
                        </TooltipContent>
                    </Tooltip>
                );
            },
        },
        {
            id: 'schedule_availability',
            accessorFn: (row) => getScheduleAvailability(row),
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Scheduled availability"
                    tooltip={
                        'Based on the caregiver\u2019s assigned shift schedule'
                    }
                />
            ),
            enableSorting: true,
            // The load-bearing column for picking someone who can actually
            // work the shift; not hideable so it can't silently vanish via
            // the persisted column toggle.
            enableHiding: false,
            enableColumnFilter: true,
            filterFn: 'inMultiSelect',
            cell: ({ row }) => {
                const scheduleAvailability = getScheduleAvailability(
                    row.original,
                );
                const { minutes: conflictMinutes, ranges: conflictRanges } =
                    getScheduleOverlap(row.original);

                if (scheduleAvailability === 'available') {
                    return (
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <Check className="h-4 w-4 text-green-600" />
                            </TooltipTrigger>
                            <TooltipContent>
                                No assigned-shift conflicts
                            </TooltipContent>
                        </Tooltip>
                    );
                }

                const hasConflictDetails =
                    conflictMinutes > 0 && conflictRanges.length > 0;
                const availableMinutes =
                    hasConflictDetails &&
                    shiftDurationMinutes !== null &&
                    shiftDurationMinutes > 0
                        ? Math.max(shiftDurationMinutes - conflictMinutes, 0)
                        : null;

                if (availableMinutes !== null && availableMinutes > 0) {
                    const shiftDuration = formatAvailabilityDuration(
                        shiftDurationMinutes!,
                    );
                    const availableRanges =
                        shiftStartTime && shiftEndTime
                            ? computeAvailableRanges(
                                  shiftStartTime,
                                  shiftEndTime,
                                  conflictRanges,
                              )
                            : [];
                    const scheduleDetailText = `${formatAvailabilityDuration(availableMinutes)}/${shiftDuration} available${availableRanges.length > 0 ? ` · ${formatOverlapRanges(availableRanges, timezone)}` : ''}`;
                    return (
                        <div className="flex max-w-[14rem] flex-col">
                            <span className="text-sm font-medium text-amber-600">
                                Partial schedule conflict
                            </span>
                            <Tooltip>
                                <TooltipTrigger asChild={true}>
                                    <span className="text-muted-foreground truncate text-xs">
                                        {scheduleDetailText}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {scheduleDetailText}
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    );
                }

                return (
                    <Tooltip>
                        <TooltipTrigger asChild={true}>
                            <span className="text-sm font-medium text-red-600">
                                Shift conflict
                            </span>
                        </TooltipTrigger>
                        <TooltipContent>Assigned-shift conflict</TooltipContent>
                    </Tooltip>
                );
            },
        },
        {
            accessorKey: 'office_name',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Office" />
            ),
            cell: ({ row }) => {
                const office = row.getValue('office_name') as
                    | string
                    | undefined;
                return (
                    <span className="text-foreground text-sm">
                        {office || '\u2014'}
                    </span>
                );
            },
            enableColumnFilter: true,
            filterFn: 'inMultiSelect',
            meta: { displayName: 'Office' },
        },
        {
            accessorKey: 'caregiver_tags',
            maxSize: 180,
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Tags" />
            ),
            cell: ({ row }) => {
                return (
                    <TagsHoverCell
                        tags={row.original.caregiver_tags ?? []}
                        patientTags={patientTags}
                    />
                );
            },
            enableColumnFilter: true,
            filterFn: 'inMultiSelect',
        },
        {
            id: 'contacts',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Contact" />
            ),
            cell: ({ row }) => {
                const contacts = row.original.contacts ?? [];
                const phoneContacts = Array.from(
                    new Map(
                        contacts
                            .filter((c) => c.type === 'phone')
                            .map((c) => [c.address, c]),
                    ).values(),
                );

                return (
                    <div className="flex flex-wrap gap-1">
                        {phoneContacts.length > 0 ? (
                            phoneContacts.map((contact, index) => (
                                <ContactBadge
                                    key={index}
                                    type="phone"
                                    address={contact.address}
                                />
                            ))
                        ) : (
                            <span className="text-foreground text-sm">
                                No phone
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'distance',
            accessorFn: (row) => row.distance ?? Number.MAX_VALUE,
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Distance" />
            ),
            enableSorting: true,
            cell: ({ row }) => {
                const distance = row.original.distance;
                return (
                    <span className="text-foreground">
                        {formatDistance(distance)}
                    </span>
                );
            },
            meta: { align: 'right', cellClassName: 'tabular-nums' },
        },
        {
            id: 'previous_visits',
            accessorFn: (row) => row.previous_visits ?? 0,
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Visits"
                    tooltip="Previous visits with this client"
                />
            ),
            enableSorting: true,
            cell: ({ row }) => {
                const visits = row.original.previous_visits;
                return (
                    <span className="text-foreground">
                        {visits !== null ? visits : '—'}
                    </span>
                );
            },
            meta: {
                displayName: 'Previous visits',
                align: 'right',
                cellClassName: 'tabular-nums',
            },
        },
        {
            id: 'contact_attempts_48h',
            accessorFn: (row) => row.contact_attempts_48h ?? 0,
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Attempts"
                    tooltip="Open shift contact attempts in the last 48 hours"
                />
            ),
            enableSorting: true,
            cell: ({ row }) => {
                const attempts = row.original.contact_attempts_48h;
                if (attempts === null) {
                    return <span className="text-foreground">—</span>;
                }
                const isHigh = attempts >= 3;
                return (
                    <span
                        className={cn(
                            'text-foreground',
                            isHigh && 'font-medium text-amber-600',
                        )}
                    >
                        {isHigh && (
                            <AlertTriangle
                                className="mr-1 inline h-3.5 w-3.5"
                                aria-hidden="true"
                            />
                        )}
                        {attempts}
                    </span>
                );
            },
            meta: {
                displayName: 'Recent contact attempts',
                align: 'right',
                cellClassName: 'tabular-nums',
            },
        },
        {
            id: 'weekly_hours',
            accessorFn: (row) => row.weekly_hours ?? 0,
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title="Hours"
                    tooltip="Scheduled hours this week, and the total if assigned"
                />
            ),
            enableSorting: true,
            cell: ({ row }) => {
                const hours = row.original.weekly_hours;
                const hoursIfAssigned = row.original.weekly_hours_if_assigned;
                const desiredHours = row.original.desired_weekly_hours;
                const isOvertime = getIsOvertime(row.original);
                const isNearLimit =
                    !isOvertime &&
                    ((overtimeHoursLimit !== null &&
                        hoursIfAssigned !== null &&
                        hoursIfAssigned >= overtimeHoursLimit * 0.9) ||
                        (desiredHours !== null &&
                            (hours ?? 0) >= desiredHours * 0.9));

                const getTextColorClass = () => {
                    if (isOvertime) {
                        return 'text-amber-600';
                    }
                    if (isNearLimit) {
                        return 'text-yellow-600';
                    }
                    return 'text-green-600';
                };

                return (
                    <div className="flex flex-col items-end">
                        <span
                            className={cn(
                                'flex items-center gap-1',
                                getTextColorClass(),
                            )}
                        >
                            {hours !== null ? hours.toFixed(1) : '—'}
                            {!isOvertime && desiredHours !== null && (
                                <span className="text-muted-foreground">
                                    {' '}
                                    / {desiredHours}
                                </span>
                            )}
                            {isOvertime && (
                                <Badge
                                    variant="secondary"
                                    className="border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-400"
                                >
                                    OT
                                </Badge>
                            )}
                        </span>
                        {hoursIfAssigned !== null && (
                            <span className="text-muted-foreground text-xs">
                                {isOvertime && overtimeHoursLimit !== null
                                    ? `${hoursIfAssigned.toFixed(1)}/${overtimeHoursLimit.toFixed(1)} hrs`
                                    : `${hoursIfAssigned.toFixed(1)} if assigned`}
                            </span>
                        )}
                    </div>
                );
            },
            meta: {
                displayName: 'Weekly hours',
                align: 'right',
                cellClassName: 'tabular-nums',
            },
        },
        {
            id: 'star_rating',
            accessorFn: (row) => row.star_rating ?? undefined,
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Rating" />
            ),
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: starRatingComparisonFilterFn,
            sortUndefined: 'last',
            cell: ({ row }) => (
                <span className="text-foreground">
                    {formatStarRating(row.original.star_rating)}
                </span>
            ),
            meta: {
                displayName: 'Star rating',
                align: 'right',
                cellClassName: 'tabular-nums',
            },
        },
        {
            id: 'gender',
            accessorFn: (row) => row.gender ?? 'unknown',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Gender" />
            ),
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: 'inMultiSelect',
            cell: ({ row }) => {
                const gender = row.original.gender;
                const displayGender =
                    gender && gender !== 'unknown'
                        ? gender.charAt(0).toUpperCase() + gender.slice(1)
                        : '—';
                return <span className="text-foreground">{displayGender}</span>;
            },
        },
        {
            id: 'primary_language',
            accessorFn: (row) => row.primary_language ?? '',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Language" />
            ),
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: 'inMultiSelect',
            sortFn: (rowA, rowB, columnId) => {
                const left = formatLanguageName(
                    rowA.getValue<string>(columnId) || null,
                );
                const right = formatLanguageName(
                    rowB.getValue<string>(columnId) || null,
                );
                return left.localeCompare(right, undefined, {
                    sensitivity: 'base',
                });
            },
            cell: ({ row }) => {
                const primaryLanguage = row.original.primary_language;
                const supportedLanguages =
                    row.original.supported_languages ?? [];
                const formattedSupported = supportedLanguages
                    .map((lang) => formatLanguageName(lang))
                    .filter((name) => name !== '—');
                return (
                    <div className="flex flex-col">
                        <span className="text-foreground">
                            {formatLanguageName(primaryLanguage)}
                        </span>
                        {formattedSupported.length > 0 && (
                            <span
                                className="text-muted-foreground text-xs"
                                title={formattedSupported.join(', ')}
                            >
                                +{formattedSupported.length} other
                                {formattedSupported.length === 1 ? '' : 's'}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'roles',
            accessorFn: (row) => row.roles ?? [],
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="License" />
            ),
            enableColumnFilter: true,
            filterFn: 'inMultiSelect',
            cell: ({ row }) => {
                const roles = row.original.roles as Array<CaregiverRole> | null;
                if (!roles || roles.length === 0) {
                    return <span className="text-foreground">—</span>;
                }
                return (
                    <div className="flex flex-wrap gap-1">
                        {roles.map((role) => (
                            <RoleBadge
                                key={role}
                                role={role}
                                className="text-xs"
                            />
                        ))}
                    </div>
                );
            },
        },
        {
            id: 'postal_code',
            accessorFn: (row) => row.postal_code ?? '',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Zip code" />
            ),
            enableColumnFilter: true,
            filterFn: 'inMultiSelect',
            cell: ({ row }) => {
                const postalCode = row.original.postal_code;
                return (
                    <span className="text-foreground text-sm">
                        {postalCode || '—'}
                    </span>
                );
            },
            meta: { displayName: 'Zip code' },
        },
        {
            id: 'overtime_status',
            accessorFn: (row) =>
                getIsOvertime(row) ? 'overtime' : 'within_limit',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="OT" />
            ),
            size: 60,
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: 'inMultiSelect',
            meta: { align: 'center' },
            cell: ({ row }) => {
                const isOvertime = getIsOvertime(row.original);
                if (isOvertime) {
                    const triggers = row.original.overtime_triggers ?? [];
                    return (
                        <Tooltip>
                            <TooltipTrigger asChild={true}>
                                <div className="flex items-center justify-center text-amber-600">
                                    <AlertTriangle className="h-4 w-4" />
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                {triggers.length === 1 && triggers[0] ? (
                                    formatTrigger(triggers[0])
                                ) : triggers.length > 1 ? (
                                    <div className="flex flex-col gap-0.5">
                                        {triggers.map((trigger) => (
                                            <span key={trigger.rule}>
                                                {formatTrigger(trigger)}
                                            </span>
                                        ))}
                                    </div>
                                ) : overtimeHoursLimit !== null ? (
                                    `Overtime over ${overtimeHoursLimit.toFixed(1)} hrs`
                                ) : (
                                    'Overtime'
                                )}
                            </TooltipContent>
                        </Tooltip>
                    );
                }
                return null;
            },
        },
        {
            id: 'is_preferred',
            accessorFn: (row) =>
                row.is_preferred ? 'preferred' : 'not_preferred',
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Preferred" />
            ),
            size: 60,
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: 'inMultiSelect',
            cell: ({ row }) => {
                if (!row.original.is_preferred) {
                    return null;
                }
                return (
                    <Tooltip>
                        <TooltipTrigger asChild={true}>
                            <div className="flex items-center justify-center">
                                <Badge variant="secondary">
                                    <Star className="h-3.5 w-3.5" />
                                </Badge>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            Preferred by this shift’s client
                        </TooltipContent>
                    </Tooltip>
                );
            },
            meta: { displayName: 'Preferred', align: 'center' },
        },
    ];
};

type InhouseRecommendationsTableProps = {
    readonly shiftId?: string;
    readonly shiftGroupId?: string;
    readonly title?: string;
    readonly onStartOutreach: InhouseStartOutreachCallback;
    readonly onSelectionChange?: InhouseSelectionChangeCallback;
    readonly showStartButton?: boolean;
    readonly buttonText?: string;
    readonly disabledCaregiverIds?: Array<string>;
    readonly preSelectedCaregiverIds?: Array<string>;
    readonly suggestedCaregiverIds?: Array<string>;
    readonly initialSelectedCaregiverIds?: Array<string>;
    /** Parent-owned selection. Prop updates do not call onSelectionChange. */
    readonly controlledSelectedCaregiverIds?: Array<string>;
    readonly defaultShowSelectedOnly?: boolean;
    readonly agentReasoningMap?: ReadonlyMap<string, string>;
    readonly isStartOutreachDisabled?: boolean;
    readonly startOutreachDisabledTooltip?: string;
    readonly shiftReadinessLabel?: string;
    readonly outreachId?: string;
    readonly persistedFeedbackByCaregiver?: Readonly<Record<string, unknown>>;
    readonly isAgentRunning?: boolean;
    readonly defaultTagFilters?: Array<string>;
    readonly mobileHeaderAction?: React.ReactNode;
    readonly outreachAgentGeneration?: 'v2' | 'v3';
    /**
     * Group membership straight from `outreach.shift_groups[]`. Selection
     * coverage counts against these ids rather than the selection rows' own
     * `group_id`, which the server nulls when a group is deleted.
     */
    readonly groupShiftIds?: ReadonlyArray<string>;
    readonly groupMode?: 'all' | 'any';
};

export function InhouseRecommendationsTable({
    shiftId,
    shiftGroupId,
    title = 'Recommended Caregivers',
    onSelectionChange,
    disabledCaregiverIds = EMPTY_DISABLED_CAREGIVER_IDS,
    preSelectedCaregiverIds,
    suggestedCaregiverIds,
    initialSelectedCaregiverIds,
    controlledSelectedCaregiverIds,
    defaultShowSelectedOnly,
    agentReasoningMap,
    outreachId,
    persistedFeedbackByCaregiver,
    isAgentRunning = false,
    defaultTagFilters,
    mobileHeaderAction,
    outreachAgentGeneration,
    groupShiftIds,
    groupMode,
}: InhouseRecommendationsTableProps) {
    const isV3BornOutreach = outreachAgentGeneration === 'v3';
    const isMobile = useIsMobile();
    const [selectedCaregiverDetailId, setSelectedCaregiverDetailId] = useState<
        string | null
    >(null);
    const [
        expandedMobileRecommendationIds,
        setExpandedMobileRecommendationIds,
    ] = useState<ReadonlySet<string>>(() => new Set());

    const parsedPersistedFeedback = useMemo(() => {
        if (!persistedFeedbackByCaregiver) {
            return undefined;
        }
        const result: Record<string, PersistedSuggestionFeedback> = {};
        for (const [id, raw] of Object.entries(persistedFeedbackByCaregiver)) {
            const entry = raw as PersistedSuggestionFeedback;
            if (typeof entry?.is_helpful === 'boolean') {
                result[id] = entry;
            }
        }
        return result;
    }, [persistedFeedbackByCaregiver]);

    const organizationId = useOrganizationId();
    const { timezone } = useOrganizationTimezone();
    const { data: organizationData } = useOrganization(organizationId, {
        reason: 'inhouse-recommendations-table',
    });
    const uiV3 = useFeatureFlag('ui_v3_components', organizationData?.features);
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        { reason: 'inhouse-recommendations-table' },
    );
    const {
        showCaregiverStatus: showEhrCaregiverStatus,
        showCompliance: showHHAExchangeCompliance,
    } = getEhrCaregiverFieldVisibility(dataSourceAccounts);
    const {
        data: organizationRecommendationDefaults,
        isLoading: isRecommendationDefaultsLoading,
    } = useOrganizationRecommendationDefaults(organizationId);
    const organizationPageSize = normalizeEligibleCaregiversPageSize(
        organizationData?.callout_settings?.eligible_caregivers_rows_per_page,
    );
    const [pageSize, setPageSize] = useState(organizationPageSize);

    const [searchQuery, setSearchQuery] = useState('');
    const hasOrgDefaults =
        organizationRecommendationDefaults !== undefined &&
        organizationRecommendationDefaults !== null;
    const orgDefaultMaxDistanceMiles =
        organizationRecommendationDefaults?.max_distance_mi ?? null;
    const orgDefaultMaxContactAttempts48h =
        organizationRecommendationDefaults?.max_contact_attempts_48h ?? null;
    const {
        maxDistanceMilesInput,
        maxDistanceMiles,
        handleMaxDistanceMilesInputChange,
        setMaxDistanceMiles,
    } = useMaxDistanceFilter({
        initialMaxDistanceMiles: orgDefaultMaxDistanceMiles,
    });
    const debouncedSearchQuery = useDebounce(searchQuery, 300);
    const [committedMaxDistanceMiles, setCommittedMaxDistanceMiles] = useState<
        number | null
    >(maxDistanceMiles);
    const [hasMaxDistanceOverride, setHasMaxDistanceOverride] = useState(false);
    const [maxContactAttempts48hInput, setMaxContactAttempts48hInput] =
        useState('');
    const [hasContactAttemptsOverride, setHasContactAttemptsOverride] =
        useState(false);
    const maxContactAttempts48h = useMemo(() => {
        const trimmed = maxContactAttempts48hInput.trim();
        if (trimmed === '') {
            return null;
        }
        const parsed = Number(trimmed);
        if (!Number.isInteger(parsed) || parsed < 1) {
            return null;
        }
        return parsed;
    }, [maxContactAttempts48hInput]);
    const effectiveMaxContactAttempts48h = hasContactAttemptsOverride
        ? maxContactAttempts48h
        : orgDefaultMaxContactAttempts48h;
    const recommendationUnitType = shiftGroupId ? 'shift_group' : 'shift';
    const recommendationUnitId = shiftGroupId ?? shiftId ?? null;
    const recommendationUnitKey = recommendationUnitId
        ? `${recommendationUnitType}:${recommendationUnitId}`
        : null;
    const filterKeyPrefix =
        outreachId && recommendationUnitKey
            ? `phoebe:recs-filters:${RECOMMENDATION_FILTER_STORAGE_VERSION}:${outreachId}:${recommendationUnitKey}`
            : undefined;
    const persistedColumnFiltersKey = filterKeyPrefix
        ? `${filterKeyPrefix}:columns`
        : null;
    // Entity-scoped facet options. Server filtering shrinks the returned rows,
    // so the toolbar's faceted filters must seed their option lists from here
    // rather than the (already-narrowed) table rows.
    const {
        data: entityFilterOptions,
        isLoading: isEntityFilterOptionsLoading,
    } = useEntityFilterOptions(
        organizationId,
        recommendationUnitType,
        recommendationUnitId,
        outreachId ?? null,
        { reason: 'inhouse-recommendations-table-filter-options' },
    );
    const facetOptions = entityFilterOptions?.options;
    const storageKey =
        outreachId && recommendationUnitKey
            ? `phoebe:outreach-selections:${outreachId}:${recommendationUnitKey}`
            : null;
    const legacyStorageKey =
        outreachId && shiftId
            ? `phoebe:outreach-selections:${outreachId}:${shiftId}`
            : null;
    const selectionStorageKeys = useMemo(() => {
        if (!storageKey) {
            return [];
        }
        if (!legacyStorageKey || legacyStorageKey === storageKey) {
            return [storageKey];
        }
        return [storageKey, legacyStorageKey];
    }, [legacyStorageKey, storageKey]);
    const dismissedCaregiverIdsQuery = useUnitDismissedCaregivers(
        organizationId,
        outreachId,
        recommendationUnitType,
        recommendationUnitId,
        { reason: 'inhouse-recommendations-table-dismissed-caregivers' },
    );
    const mutateDismissedCaregivers = dismissedCaregiverIdsQuery.mutate;
    const [dismissedCaregiverIdOverrides, setDismissedCaregiverIdOverrides] =
        useState<ReadonlyMap<string, boolean>>(() => new Map());
    // A v3 uncheck that routes to dismiss must grey the row now, not on the
    // next navigation: override locally and refetch the dismissed set.
    const handleV3DismissalsPersisted = useCallback(
        (caregiverIds: ReadonlyArray<string>) => {
            setDismissedCaregiverIdOverrides((previousOverrides) => {
                const nextOverrides = new Map(previousOverrides);
                for (const caregiverId of caregiverIds) {
                    nextOverrides.set(caregiverId, true);
                }
                return nextOverrides;
            });
            void mutateDismissedCaregivers();
        },
        [mutateDismissedCaregivers],
    );
    const v3Selection = useOutreachV3SelectionState({
        organizationId,
        outreachId,
        unitType: recommendationUnitType,
        unitId: recommendationUnitId,
        unitShiftIds: groupShiftIds,
        enabled: isV3BornOutreach,
        onDismissalsPersisted: handleV3DismissalsPersisted,
    });
    // Bound out of the hook result so the write callback below depends on
    // this one stable member rather than the whole object.
    const persistV3SelectionDelta = v3Selection.persistSelectionDelta;
    const persistV3ShiftSelection = v3Selection.persistShiftSelection;
    const shouldLoadDismissedCaregivers = Boolean(
        organizationId && outreachId && recommendationUnitId,
    );
    const hasLoadedDismissedCaregivers =
        (!shouldLoadDismissedCaregivers ||
            dismissedCaregiverIdsQuery.data !== undefined ||
            dismissedCaregiverIdsQuery.error !== undefined) &&
        v3Selection.isReady;
    const dismissedCaregiverIds = useMemo(() => {
        const ids = new Set(dismissedCaregiverIdsQuery.data ?? []);
        for (const [
            caregiverId,
            isDismissed,
        ] of dismissedCaregiverIdOverrides) {
            if (isDismissed) {
                ids.add(caregiverId);
            } else {
                ids.delete(caregiverId);
            }
        }
        return ids;
    }, [dismissedCaregiverIdOverrides, dismissedCaregiverIdsQuery.data]);
    const suggestedCaregiverIdSet = useMemo(
        () => new Set(suggestedCaregiverIds ?? []),
        [suggestedCaregiverIds],
    );
    const [localSelectedCaregiverIds, setLocalSelectedCaregiverIds] = useState<
        Set<string>
    >(() => {
        if (!hasLoadedDismissedCaregivers) {
            return new Set();
        }
        return buildInitialSelectedCaregiverIds({
            dismissedCaregiverIds,
            initialSelectedCaregiverIds:
                controlledSelectedCaregiverIds ?? initialSelectedCaregiverIds,
            preSelectedCaregiverIds,
            storageKeys: selectionStorageKeys,
        });
    });
    // A v3-born outreach's checkboxes are the server's STAGED rows plus the
    // unconfirmed local deltas — never a once-seeded local snapshot, or a
    // repaint from the runner would not reach an open page.
    const selectedCaregiverIds: ReadonlySet<string> = isV3BornOutreach
        ? v3Selection.stagedCaregiverIds
        : localSelectedCaregiverIds;
    const selectedCaregiverIdsRef =
        useRef<ReadonlySet<string>>(selectedCaregiverIds);
    const pendingControlledSelectionRef = useRef<Set<string> | null>(
        controlledSelectedCaregiverIds === undefined
            ? null
            : localSelectedCaregiverIds,
    );
    useEffect(() => {
        selectedCaregiverIdsRef.current = selectedCaregiverIds;
    }, [selectedCaregiverIds]);
    // The ref guards the effect synchronously; the state is what the grouping
    // snapshot below waits on, since a ref cannot re-anchor it on hydration.
    const hasAppliedInitialSelectionRef = useRef(hasLoadedDismissedCaregivers);
    const [hasHydratedSelection, setHasHydratedSelection] = useState(
        hasLoadedDismissedCaregivers,
    );
    const [showSelectedOnly, setShowSelectedOnly] = useState(
        () => defaultShowSelectedOnly ?? false,
    );
    const [showSelectedCaregiverIds, setShowSelectedCaregiverIds] = useState<
        Array<string>
    >(() =>
        defaultShowSelectedOnly
            ? buildShowSelectedCaregiverIdsSnapshot({
                  dismissedCaregiverIds,
                  preSelectedCaregiverIds,
                  selectedCaregiverIds,
              })
            : [],
    );
    const tableRef = useRef<TableType<InhouseRecommendation> | null>(null);
    const lastClickedCaregiverIdRef = useRef<string | null>(null);
    const hasPendingUserSelectionChangeRef = useRef(false);
    const isApplyingColumnFiltersRef = useRef(false);
    const defaultColumnFilterKeysRef = useRef<Set<string>>(new Set());
    const initialSelectedIdsRef = useRef(
        new Set(
            controlledSelectedCaregiverIds ?? initialSelectedCaregiverIds ?? [],
        ),
    );
    // Parent-driven changes reconcile in an effect below. Do not publish the
    // previous internal selection while that newer selection is still pending.
    const selectionProp =
        controlledSelectedCaregiverIds ?? initialSelectedCaregiverIds;
    const hasPendingSelectionPropUpdate =
        selectionProp !== undefined &&
        !areSetsEqual(initialSelectedIdsRef.current, new Set(selectionProp));
    const preSelectedIdsRef = useRef(new Set(preSelectedCaregiverIds ?? []));
    const seenPreSelectedIdsRef = useRef(
        new Set(preSelectedCaregiverIds ?? []),
    );
    const caregiverNameCacheRef = useRef(new Map<string, string>());
    const [activeColumnFilters, setActiveColumnFilters] =
        useState<ColumnFiltersState>(() =>
            buildRecommendationInitialColumnFilters({
                defaults: undefined,
                patientTags: [],
                patientOfficeName: null,
                defaultTagFilters: undefined,
            }),
        );
    const applyTableColumnFilters = useCallback(
        (filters: ColumnFiltersState) => {
            if (!tableRef.current) {
                return;
            }
            isApplyingColumnFiltersRef.current = true;
            try {
                tableRef.current.setColumnFilters(filters);
            } finally {
                isApplyingColumnFiltersRef.current = false;
            }
        },
        [],
    );
    const effectiveMaxDistanceMiles = hasMaxDistanceOverride
        ? committedMaxDistanceMiles
        : orgDefaultMaxDistanceMiles;
    const isSearchBypassingFilters = debouncedSearchQuery.trim().length > 0;
    const handleSearchQueryChange = useCallback((nextSearchQuery: string) => {
        setSearchQuery(nextSearchQuery);
    }, []);

    // Additions only: dropping a deselected caregiver here would make the
    // row vanish under "show selected", so the coordinator loses the row
    // they just unchecked. It stays visible and unchecked until the snapshot
    // is rebuilt from scratch.
    const applyExternalSelectionDeltaToShowSelected = useCallback(
        (addedIds: Array<string>) => {
            if (
                addedIds.length === 0 ||
                !defaultShowSelectedOnly ||
                !showSelectedOnly
            ) {
                return;
            }
            setShowSelectedCaregiverIds((previous) =>
                normalizeSelectedCaregiverIds([...previous, ...addedIds]),
            );
        },
        [defaultShowSelectedOnly, showSelectedOnly],
    );

    useEffect(() => {
        if (
            !hasLoadedDismissedCaregivers ||
            hasAppliedInitialSelectionRef.current
        ) {
            return;
        }
        // v3-born checkboxes are derived from the server rows, so there is
        // no local snapshot to seed — only the "show selected" snapshot.
        if (isV3BornOutreach) {
            if (defaultShowSelectedOnly) {
                setShowSelectedCaregiverIds(
                    buildShowSelectedCaregiverIdsSnapshot({
                        dismissedCaregiverIds,
                        preSelectedCaregiverIds,
                        selectedCaregiverIds: v3Selection.stagedCaregiverIds,
                    }),
                );
            }
            hasAppliedInitialSelectionRef.current = true;
            setHasHydratedSelection(true);
            return;
        }
        const nextSelectedIds = buildInitialSelectedCaregiverIds({
            dismissedCaregiverIds,
            initialSelectedCaregiverIds:
                controlledSelectedCaregiverIds ?? initialSelectedCaregiverIds,
            preSelectedCaregiverIds,
            storageKeys: selectionStorageKeys,
        });
        if (controlledSelectedCaregiverIds !== undefined) {
            pendingControlledSelectionRef.current = nextSelectedIds;
        }
        selectedCaregiverIdsRef.current = nextSelectedIds;
        setLocalSelectedCaregiverIds(nextSelectedIds);
        if (defaultShowSelectedOnly) {
            setShowSelectedCaregiverIds(
                buildShowSelectedCaregiverIdsSnapshot({
                    dismissedCaregiverIds,
                    preSelectedCaregiverIds,
                    selectedCaregiverIds: nextSelectedIds,
                }),
            );
        }
        hasAppliedInitialSelectionRef.current = true;
        setHasHydratedSelection(true);
    }, [
        defaultShowSelectedOnly,
        controlledSelectedCaregiverIds,
        dismissedCaregiverIds,
        hasLoadedDismissedCaregivers,
        initialSelectedCaregiverIds,
        isV3BornOutreach,
        preSelectedCaregiverIds,
        selectionStorageKeys,
        v3Selection.stagedCaregiverIds,
    ]);

    const selectedCaregiverIdsForRequest = useMemo(
        () => normalizeSelectedCaregiverIds(Array.from(selectedCaregiverIds)),
        [selectedCaregiverIds],
    );
    const persistUserSelectionDelta = useCallback(
        (previous: ReadonlySet<string>, next: ReadonlySet<string>) => {
            if (!organizationId || !outreachId || !recommendationUnitId) {
                return;
            }

            const addedIds = Array.from(next).filter((id) => !previous.has(id));
            const removedIds = Array.from(previous).filter(
                (id) => !next.has(id),
            );
            if (addedIds.length === 0 && removedIds.length === 0) {
                return;
            }
            if (isV3BornOutreach) {
                if (addedIds.length > 0) {
                    // Re-checking a caregiver lifts the local dismissal grey
                    // immediately; the dismiss side is set post-commit.
                    setDismissedCaregiverIdOverrides((previousOverrides) => {
                        const nextOverrides = new Map(previousOverrides);
                        for (const caregiverId of addedIds) {
                            nextOverrides.set(caregiverId, false);
                        }
                        return nextOverrides;
                    });
                }
                persistV3SelectionDelta({ addedIds, removedIds });
                return;
            }
            const changedIds = [...addedIds, ...removedIds];

            setDismissedCaregiverIdOverrides((previousOverrides) => {
                const nextOverrides = new Map(previousOverrides);
                for (const caregiverId of addedIds) {
                    nextOverrides.set(caregiverId, false);
                }
                for (const caregiverId of removedIds) {
                    nextOverrides.set(caregiverId, true);
                }
                return nextOverrides;
            });

            void mutateDismissedCaregivers(
                (current) => {
                    if (!current) {
                        return current;
                    }

                    const addedIdSet = new Set(addedIds);
                    const removedIdSet = new Set(removedIds);
                    const dismissedCaregivers =
                        current.dismissed_caregivers.filter(
                            (caregiver) =>
                                !addedIdSet.has(caregiver.caregiver_id),
                        );
                    const dismissedCaregiverIdsInCache = new Set(
                        dismissedCaregivers.map(
                            (caregiver) => caregiver.caregiver_id,
                        ),
                    );
                    const dismissedAt = new Date().toISOString();
                    for (const caregiverId of removedIds) {
                        if (!dismissedCaregiverIdsInCache.has(caregiverId)) {
                            dismissedCaregivers.push({
                                caregiver_id: caregiverId,
                                dismissed_at: dismissedAt,
                            });
                        }
                    }

                    return {
                        ...current,
                        dismissed_caregivers: dismissedCaregivers.filter(
                            (caregiver) =>
                                removedIdSet.has(caregiver.caregiver_id) ||
                                !addedIdSet.has(caregiver.caregiver_id),
                        ),
                    };
                },
                { revalidate: false },
            );

            const rollBackSelectionDelta = () => {
                setDismissedCaregiverIdOverrides((previousOverrides) => {
                    const nextOverrides = new Map(previousOverrides);
                    for (const caregiverId of changedIds) {
                        nextOverrides.delete(caregiverId);
                    }
                    return nextOverrides;
                });
                void mutateDismissedCaregivers();
                toast.error('Could not save caregiver selection changes.', {
                    persistNotification: false,
                });
            };

            void batchUpdateRecommendationCaregiverSelections({
                organizationId,
                outreachId,
                unitType: recommendationUnitType,
                unitId: recommendationUnitId,
                items: [
                    ...addedIds.map((caregiverId) => ({
                        caregiver_id: caregiverId,
                        action: 'undismiss' as const,
                    })),
                    ...removedIds.map((caregiverId) => ({
                        caregiver_id: caregiverId,
                        action: 'dismiss' as const,
                    })),
                ],
            }).then(
                (response) => {
                    if (
                        response.items.some((item) => item.error_code !== null)
                    ) {
                        rollBackSelectionDelta();
                    }
                },
                () => {
                    rollBackSelectionDelta();
                },
            );
        },
        [
            isV3BornOutreach,
            mutateDismissedCaregivers,
            organizationId,
            outreachId,
            persistV3SelectionDelta,
            recommendationUnitId,
            recommendationUnitType,
        ],
    );
    const setUserSelectedCaregiverIds = useCallback(
        (value: Set<string> | ((previous: Set<string>) => Set<string>)) => {
            const previous = isV3BornOutreach
                ? v3Selection.stagedCaregiverIds
                : selectedCaregiverIdsRef.current;
            const next =
                typeof value === 'function' ? value(new Set(previous)) : value;
            const nextSelectedCaregiverIds = new Set(next);
            if (areSetsEqual(previous, nextSelectedCaregiverIds)) {
                return;
            }
            persistUserSelectionDelta(previous, nextSelectedCaregiverIds);
            if (isV3BornOutreach) {
                return;
            }
            selectedCaregiverIdsRef.current = nextSelectedCaregiverIds;
            setLocalSelectedCaregiverIds(nextSelectedCaregiverIds);
        },
        [
            isV3BornOutreach,
            persistUserSelectionDelta,
            v3Selection.stagedCaregiverIds,
        ],
    );

    // Force-include recommendations, controlled selections, dismissed
    // caregivers, and the "Show selected" snapshot so filters can't drop
    // them. Ordinary checkbox selections still follow the active filters.
    const alwaysIncludeCaregiverIds = useMemo(() => {
        const ids = new Set(preSelectedCaregiverIds ?? []);
        for (const id of controlledSelectedCaregiverIds ??
            initialSelectedCaregiverIds ??
            []) {
            ids.add(id);
        }
        for (const id of dismissedCaregiverIds) {
            ids.add(id);
        }
        for (const id of suggestedCaregiverIdSet) {
            ids.add(id);
        }
        if (showSelectedOnly) {
            for (const id of showSelectedCaregiverIds) {
                ids.add(id);
            }
        }
        // Sorted so the fetch key stays stable across re-renders.
        return ids.size > 0 ? [...ids].sort() : undefined;
    }, [
        controlledSelectedCaregiverIds,
        dismissedCaregiverIds,
        initialSelectedCaregiverIds,
        preSelectedCaregiverIds,
        showSelectedOnly,
        showSelectedCaregiverIds,
        suggestedCaregiverIdSet,
    ]);

    const recommendationRequestParams = useMemo(
        () =>
            isSearchBypassingFilters
                ? buildInhouseRecommendationV2RequestParamsFromFacets({
                      outreachId,
                      alwaysIncludeCaregiverIds,
                      columnFilters: [],
                      maxDistanceMi: null,
                      maxContactAttempts: null,
                      minStarRating: null,
                      maxStarRating: null,
                  })
                : buildInhouseRecommendationV2RequestParamsFromFacets({
                      outreachId,
                      alwaysIncludeCaregiverIds,
                      columnFilters: activeColumnFilters,
                      maxDistanceMi: effectiveMaxDistanceMiles,
                      maxContactAttempts: effectiveMaxContactAttempts48h,
                      minStarRating:
                          getMinStarRatingFromColumnFilters(
                              activeColumnFilters,
                          ),
                      maxStarRating:
                          getMaxStarRatingFromColumnFilters(
                              activeColumnFilters,
                          ),
                  }),
        [
            activeColumnFilters,
            alwaysIncludeCaregiverIds,
            effectiveMaxContactAttempts48h,
            isSearchBypassingFilters,
            outreachId,
            effectiveMaxDistanceMiles,
        ],
    );
    const recommendationsOrganizationId =
        isRecommendationDefaultsLoading ||
        !hasLoadedDismissedCaregivers ||
        isEntityFilterOptionsLoading
            ? null
            : organizationId;

    const singleShiftQuery = useInhouseRecommendations(
        shiftGroupId ? null : recommendationsOrganizationId,
        shiftGroupId ? null : (shiftId ?? null),
        { reason: 'inhouse-recommendations-table' },
        recommendationRequestParams,
    );
    const groupQuery = useGroupInhouseRecommendations(
        shiftGroupId ? recommendationsOrganizationId : null,
        shiftGroupId ?? null,
        outreachId ?? null,
        { reason: 'inhouse-recommendations-table-group' },
        recommendationRequestParams,
    );
    const isLoading =
        !hasLoadedDismissedCaregivers ||
        (shiftGroupId ? groupQuery.isLoading : singleShiftQuery.isLoading);
    const error = shiftGroupId ? groupQuery.error : singleShiftQuery.error;
    const inhouseRecommendationsResponse = useMemo<
        InhouseRecommendationsResponse | undefined
    >(() => {
        if (shiftGroupId) {
            const groupResponse = groupQuery.data as
                | {
                      readonly recommendations?: Array<InhouseRecommendation>;
                      readonly group_mode?: 'all' | 'any';
                      readonly total_shift_duration_minutes?: number;
                      readonly suggested_filter_relaxations?: ReadonlyArray<string>;
                      readonly shifts?: ReadonlyArray<{
                          readonly shift_id: string;
                          readonly shift_start_time: string;
                          readonly shift_end_time: string;
                          readonly patient_office_name?: string | null;
                          readonly patient_tags?: ReadonlyArray<string> | null;
                          readonly client_id?: string | null;
                      }>;
                  }
                | undefined;
            if (!groupResponse) {
                return undefined;
            }
            const firstGroupShift = groupResponse.shifts?.[0];
            const groupShifts = groupResponse.shifts ?? [];
            // Dedupe one shift_id per unique client so the recalc fan-out
            // re-geocodes each distinct client once. Shifts with a null
            // client_id are skipped intentionally — the backend recalc
            // endpoint returns 422 for them, so including them would
            // produce spurious errors. If no shift has client_id at all,
            // fall back to per-shift fan-out (older backend deployments).
            const haveClientIds = groupShifts.some(
                (groupShift) =>
                    groupShift.client_id !== null &&
                    groupShift.client_id !== undefined,
            );
            const dedupedShiftIds: Array<string> = [];
            if (haveClientIds) {
                const seenClientIds = new Set<string>();
                for (const groupShift of groupShifts) {
                    if (
                        groupShift.client_id === null ||
                        groupShift.client_id === undefined ||
                        seenClientIds.has(groupShift.client_id)
                    ) {
                        continue;
                    }
                    seenClientIds.add(groupShift.client_id);
                    dedupedShiftIds.push(groupShift.shift_id);
                }
            } else {
                for (const groupShift of groupShifts) {
                    dedupedShiftIds.push(groupShift.shift_id);
                }
            }
            const clientIds = groupShifts
                .map((groupShift) => groupShift.client_id)
                .filter(
                    (clientId): clientId is string =>
                        clientId !== null && clientId !== undefined,
                );
            const isSingleClientGroup =
                new Set(clientIds).size <= 1 && clientIds.length > 0;
            const shouldUseUnionClientMetadata =
                groupResponse.group_mode === 'all' && !isSingleClientGroup;
            const groupPatientTags =
                isSingleClientGroup || shouldUseUnionClientMetadata
                    ? Array.from(
                          new Set(
                              groupShifts.flatMap(
                                  (groupShift) => groupShift.patient_tags ?? [],
                              ),
                          ),
                      )
                    : null;
            const uniquePatientOffices = Array.from(
                new Set(
                    groupShifts
                        .map((groupShift) => groupShift.patient_office_name)
                        .filter(
                            (officeName): officeName is string =>
                                officeName !== null &&
                                officeName !== undefined &&
                                officeName.trim().length > 0,
                        )
                        .map((officeName) => officeName.trim()),
                ),
            );
            return {
                shift_group_id: shiftGroupId,
                recommendations: groupResponse.recommendations,
                shift_duration_minutes:
                    groupResponse.total_shift_duration_minutes,
                shift_start_time: firstGroupShift?.shift_start_time,
                shift_end_time: firstGroupShift?.shift_end_time,
                patient_office_name:
                    uniquePatientOffices.length === 1
                        ? uniquePatientOffices[0]
                        : null,
                patient_tags: groupPatientTags,
                shift_ids: dedupedShiftIds,
                suggested_filter_relaxations:
                    groupResponse.suggested_filter_relaxations,
            };
        }
        return singleShiftQuery.data as
            | InhouseRecommendationsResponse
            | undefined;
    }, [shiftGroupId, groupQuery.data, singleShiftQuery.data]);

    const suggestedRelaxationLabels = useMemo(() => {
        const response = inhouseRecommendationsResponse;
        // The engine sends suggestions whenever nothing passes the
        // filters, but force-included caregivers can still be visible —
        // the "No matches" banner would contradict their rows, so only
        // show it when the table is truly empty.
        if (!response || (response.recommendations ?? []).length > 0) {
            return [];
        }
        return (response.suggested_filter_relaxations ?? []).map((filter) =>
            formatFilterRelaxationLabel(filter),
        );
    }, [inhouseRecommendationsResponse]);

    const recalcShiftIds = useMemo<ReadonlyArray<string>>(() => {
        if (shiftGroupId) {
            return inhouseRecommendationsResponse?.shift_ids ?? [];
        }
        if (!shiftId) {
            return [];
        }
        return [shiftId];
    }, [shiftGroupId, inhouseRecommendationsResponse?.shift_ids, shiftId]);

    // Only ANY groups can be partially covered. An ALL group is whole-group
    // by invariant, so a count there would imply a subset is editable.
    const selectionCoverage = useMemo<
        OutreachSelectionCoverage | undefined
    >(() => {
        if (
            !isV3BornOutreach ||
            groupMode !== 'any' ||
            !groupShiftIds ||
            groupShiftIds.length === 0
        ) {
            return undefined;
        }
        return {
            shiftIds: groupShiftIds,
            shiftLabelById: new Map(
                (groupQuery.data?.shifts ?? []).map((groupShift) => [
                    groupShift.shift_id,
                    formatDateAndTime(groupShift.shift_start_time, timezone),
                ]),
            ),
            stagedShiftIdsByCaregiverId:
                v3Selection.selectionCoverageByCaregiverId,
            onSelectEveryShift: (caregiverId) =>
                persistV3SelectionDelta({
                    addedIds: [caregiverId],
                    removedIds: [],
                }),
        };
    }, [
        groupMode,
        groupQuery.data?.shifts,
        groupShiftIds,
        isV3BornOutreach,
        persistV3SelectionDelta,
        timezone,
        v3Selection.selectionCoverageByCaregiverId,
    ]);

    // Cache caregiver names across API responses so that selection
    // callbacks can resolve names for caregivers that are currently
    // filtered out of the visible recommendations.
    useEffect(() => {
        const recs = inhouseRecommendationsResponse?.recommendations;
        if (!recs) {
            return;
        }
        for (const rec of recs) {
            caregiverNameCacheRef.current.set(
                rec.caregiver_id,
                rec.caregiver_name,
            );
        }
    }, [inhouseRecommendationsResponse?.recommendations]);

    const shiftDurationMinutes =
        inhouseRecommendationsResponse?.shift_duration_minutes ?? null;
    const shiftStartTime =
        inhouseRecommendationsResponse?.shift_start_time ?? null;
    const shiftEndTime = inhouseRecommendationsResponse?.shift_end_time ?? null;
    const patientTags = useMemo(
        () =>
            (inhouseRecommendationsResponse?.patient_tags ?? [])
                .map((tag) => tag.trim())
                .filter((tag) => tag.length > 0),
        [inhouseRecommendationsResponse?.patient_tags],
    );
    const entityDefaults = entityFilterOptions?.defaults;
    const initialColumnFilters = useMemo<ColumnFiltersState>(() => {
        const seed = entityDefaults
            ? buildEntityDefaultColumnFilters(entityDefaults)
            : [];
        return reconcileColumnFiltersAgainstFacets(seed, facetOptions);
    }, [entityDefaults, facetOptions]);
    const defaultFiltersFingerprint = JSON.stringify(
        normalizeColumnFilters(initialColumnFilters),
    );
    useEffect(() => {
        const ids = initialColumnFilters.map((filter) => filter.id);
        const keys =
            recommendationUnitType === 'shift_group'
                ? ids.filter(
                      (id) => id !== 'caregiver_tags' && id !== 'office_name',
                  )
                : ids;
        defaultColumnFilterKeysRef.current = new Set(keys);
    }, [initialColumnFilters, recommendationUnitType]);
    const filterDefaultsSyncKey =
        entityFilterOptions === undefined
            ? 'server-defaults:loading'
            : `server-defaults:${defaultFiltersFingerprint}`;
    // Identity of "which entity + which defaults" the seed effect below has
    // already applied. Also gates persistence: filter-change notifications
    // that arrive before this entity was seeded (e.g. DataTable's mount-time
    // notify echoing initial filters) must not be written as user changes.
    const filterSeedSyncToken = `${persistedColumnFiltersKey ?? 'none'}|${filterDefaultsSyncKey}`;
    const dataTableKey = recommendationUnitKey;
    const tableInitialColumnFilters = useMemo<ColumnFiltersState>(
        () => initialColumnFilters ?? [],
        [initialColumnFilters],
    );
    const lastSyncedDataTableKeyRef = useRef<string | null>(null);
    const dataTableInitialColumnFilters = tableInitialColumnFilters;

    // Restore the persisted set (including a cleared []); seed defaults only
    // when there is no persisted entry, so a reload never wipes the set.
    useEffect(() => {
        if (lastSyncedDataTableKeyRef.current === filterSeedSyncToken) {
            return;
        }
        const persisted = readPersistedColumnFilters(persistedColumnFiltersKey);
        // Wait for defaults before seeding a fresh entity, else we persist []
        // and pre-empt the real defaults.
        if (persisted === null && entityFilterOptions === undefined) {
            return;
        }
        lastSyncedDataTableKeyRef.current = filterSeedSyncToken;
        const base = persisted ?? tableInitialColumnFilters;
        const next = reconcileColumnFiltersAgainstFacets(base, facetOptions);
        setActiveColumnFilters(next);
        applyTableColumnFilters(next);
        if (persisted === null) {
            setHasMaxDistanceOverride(false);
        }
    }, [
        applyTableColumnFilters,
        entityFilterOptions,
        facetOptions,
        filterSeedSyncToken,
        persistedColumnFiltersKey,
        tableInitialColumnFilters,
    ]);

    const handleColumnFiltersChange = useCallback(
        (nextFilters: ColumnFiltersState) => {
            if (isApplyingColumnFiltersRef.current) {
                setActiveColumnFilters(nextFilters);
                return;
            }

            // v2 persistence is parent-owned and written only on user-driven
            // changes, so a fresh outreach keeps no entry (= seed defaults)
            // until the user actually touches a filter. Programmatic applies
            // come through isApplyingColumnFiltersRef above, and anything
            // arriving before this entity was seeded (DataTable's mount-time
            // notify can echo cached defaults ahead of the seed effect) is
            // not a user change either.
            if (
                lastSyncedDataTableKeyRef.current === filterSeedSyncToken &&
                !areColumnFiltersEqual(nextFilters, activeColumnFilters)
            ) {
                writePersistedColumnFilters(
                    persistedColumnFiltersKey,
                    nextFilters,
                );
            }

            setActiveColumnFilters(nextFilters);
        },
        [activeColumnFilters, filterSeedSyncToken, persistedColumnFiltersKey],
    );

    useEffect(() => {
        if (!facetOptions) {
            return;
        }
        const reconciled = reconcileColumnFiltersAgainstFacets(
            activeColumnFilters,
            facetOptions,
        );
        if (!areColumnFiltersEqual(reconciled, activeColumnFilters)) {
            setActiveColumnFilters(reconciled);
            applyTableColumnFilters(reconciled);
        }
    }, [activeColumnFilters, applyTableColumnFilters, facetOptions]);

    const [
        suggestionFeedbackByCaregiverId,
        setSuggestionFeedbackByCaregiverId,
    ] = useState<Record<string, SuggestionFeedback>>({});
    const [
        persistedFeedbackByCaregiverState,
        setPersistedFeedbackByCaregiverState,
    ] = useState<Record<string, PersistedSuggestionFeedback>>({});
    const hasInitializedFromPersisted = useRef(false);
    const onSelectionChangeRef = useRef(onSelectionChange);
    onSelectionChangeRef.current = onSelectionChange;

    useEffect(() => {
        if (
            hasInitializedFromPersisted.current ||
            !parsedPersistedFeedback ||
            !hasLoadedDismissedCaregivers
        ) {
            return;
        }
        hasInitializedFromPersisted.current = true;
        const initial: Record<string, SuggestionFeedback> = {};
        const downvotedIds: Array<string> = [];
        for (const [id, entry] of Object.entries(parsedPersistedFeedback)) {
            initial[id] = entry.is_helpful ? 'up' : 'down';
            if (!entry.is_helpful) {
                downvotedIds.push(id);
            }
        }
        setPersistedFeedbackByCaregiverState(parsedPersistedFeedback);
        setSuggestionFeedbackByCaregiverId(initial);
        if (downvotedIds.length > 0) {
            const nextSelectedIds = new Set(selectedCaregiverIdsRef.current);
            for (const id of downvotedIds) {
                nextSelectedIds.delete(id);
            }
            if (pendingControlledSelectionRef.current !== null) {
                pendingControlledSelectionRef.current = nextSelectedIds;
            }
            selectedCaregiverIdsRef.current = nextSelectedIds;
            setLocalSelectedCaregiverIds(nextSelectedIds);
        }
    }, [hasLoadedDismissedCaregivers, parsedPersistedFeedback]);

    useEffect(() => {
        if (!hasLoadedDismissedCaregivers) {
            return;
        }
        if (!storageKey) {
            return;
        }
        try {
            localStorage.setItem(
                storageKey,
                JSON.stringify({
                    suggestions: preSelectedCaregiverIds ?? [],
                    selected: Array.from(selectedCaregiverIds),
                }),
            );
        } catch {
            // Private browsing or quota exceeded
        }
    }, [
        hasLoadedDismissedCaregivers,
        storageKey,
        selectedCaregiverIds,
        preSelectedCaregiverIds,
    ]);

    useEffect(() => {
        if (!hasLoadedDismissedCaregivers) {
            return;
        }
        const nextInitialSelectedIds = new Set(
            controlledSelectedCaregiverIds ?? initialSelectedCaregiverIds ?? [],
        );
        const previousInitialSelectedIds = initialSelectedIdsRef.current;
        const addedIds = Array.from(nextInitialSelectedIds).filter(
            (id) =>
                !previousInitialSelectedIds.has(id) &&
                !dismissedCaregiverIds.has(id),
        );
        const removedIds = Array.from(previousInitialSelectedIds).filter(
            (id) => !nextInitialSelectedIds.has(id),
        );

        if (addedIds.length > 0 || removedIds.length > 0) {
            const nextSelectedIds = new Set(selectedCaregiverIdsRef.current);
            for (const id of addedIds) {
                nextSelectedIds.add(id);
            }
            for (const id of removedIds) {
                nextSelectedIds.delete(id);
            }
            if (controlledSelectedCaregiverIds !== undefined) {
                pendingControlledSelectionRef.current = nextSelectedIds;
            }
            selectedCaregiverIdsRef.current = nextSelectedIds;
            setLocalSelectedCaregiverIds(nextSelectedIds);
            applyExternalSelectionDeltaToShowSelected(addedIds);
        }

        initialSelectedIdsRef.current = nextInitialSelectedIds;
    }, [
        applyExternalSelectionDeltaToShowSelected,
        controlledSelectedCaregiverIds,
        dismissedCaregiverIds,
        hasLoadedDismissedCaregivers,
        initialSelectedCaregiverIds,
    ]);

    useEffect(() => {
        if (!hasLoadedDismissedCaregivers) {
            return;
        }
        if (!preSelectedCaregiverIds) {
            return;
        }
        const nextPreSelectedIds = new Set(preSelectedCaregiverIds);
        const prevPreSelectedIds = preSelectedIdsRef.current;
        const seenPreSelectedIds = seenPreSelectedIdsRef.current;
        const addedIds = preSelectedCaregiverIds.filter(
            (id) =>
                !seenPreSelectedIds.has(id) && !dismissedCaregiverIds.has(id),
        );
        const removedIds = Array.from(prevPreSelectedIds).filter(
            (id) => !nextPreSelectedIds.has(id),
        );

        if (addedIds.length > 0 || removedIds.length > 0) {
            const nextSelectedIds = new Set(selectedCaregiverIdsRef.current);
            for (const id of addedIds) {
                nextSelectedIds.add(id);
            }
            for (const id of removedIds) {
                nextSelectedIds.delete(id);
            }
            if (pendingControlledSelectionRef.current !== null) {
                pendingControlledSelectionRef.current = nextSelectedIds;
            }
            selectedCaregiverIdsRef.current = nextSelectedIds;
            setLocalSelectedCaregiverIds(nextSelectedIds);
            applyExternalSelectionDeltaToShowSelected(addedIds);
            if (defaultShowSelectedOnly && showSelectedOnly) {
                // Union, never replace: rebuilding from the current
                // pre-selected set would vanish the row a coordinator just
                // unchecked. It stays until the toggle rebuilds the snapshot.
                setShowSelectedCaregiverIds((previous) =>
                    normalizeSelectedCaregiverIds([
                        ...previous,
                        ...preSelectedCaregiverIds,
                    ]),
                );
            }
        }
        preSelectedIdsRef.current = nextPreSelectedIds;
        seenPreSelectedIdsRef.current = new Set([
            ...seenPreSelectedIds,
            ...nextPreSelectedIds,
        ]);
    }, [
        applyExternalSelectionDeltaToShowSelected,
        defaultShowSelectedOnly,
        dismissedCaregiverIds,
        hasLoadedDismissedCaregivers,
        preSelectedCaregiverIds,
        showSelectedOnly,
    ]);

    const selectedVisibleCaregiverNamesById = useMemo(() => {
        const recommendations =
            inhouseRecommendationsResponse?.recommendations ?? [];
        return Object.fromEntries(
            recommendations
                .filter((r) => selectedCaregiverIds.has(r.caregiver_id))
                .map((r) => [r.caregiver_id, r.caregiver_name]),
        );
    }, [selectedCaregiverIds, inhouseRecommendationsResponse]);

    useEffect(() => {
        if (!hasLoadedDismissedCaregivers || hasPendingSelectionPropUpdate) {
            return;
        }
        const controlledSelection = pendingControlledSelectionRef.current;
        if (controlledSelection !== null) {
            if (areSetsEqual(selectedCaregiverIds, controlledSelection)) {
                pendingControlledSelectionRef.current = null;
            }
            return;
        }
        onSelectionChangeRef.current?.(
            selectedCaregiverIdsForRequest,
            selectedVisibleCaregiverNamesById,
        );
    }, [
        hasLoadedDismissedCaregivers,
        hasPendingSelectionPropUpdate,
        selectedCaregiverIds,
        selectedCaregiverIdsForRequest,
        selectedVisibleCaregiverNamesById,
    ]);

    useEffect(() => {
        setPageSize(organizationPageSize);
    }, [organizationPageSize]);

    const handleResetToOrgDefaults = useCallback(() => {
        const resetFilters = initialColumnFilters ?? [];
        // Drop the per-outreach entry so this outreach follows the org
        // defaults again instead of pinning a snapshot of them.
        removePersistedColumnFilters(persistedColumnFiltersKey);
        setActiveColumnFilters(resetFilters);
        applyTableColumnFilters(resetFilters);
        setHasMaxDistanceOverride(false);
        setMaxDistanceMiles(orgDefaultMaxDistanceMiles);
        setCommittedMaxDistanceMiles(orgDefaultMaxDistanceMiles);
        setHasContactAttemptsOverride(false);
        setMaxContactAttempts48hInput('');
    }, [
        applyTableColumnFilters,
        initialColumnFilters,
        orgDefaultMaxDistanceMiles,
        persistedColumnFiltersKey,
        setMaxDistanceMiles,
    ]);

    const toggleShowSelectedOnly = useCallback(() => {
        if (selectedCaregiverIdsForRequest.length === 0 && !showSelectedOnly) {
            return;
        }
        // v2 keeps the applied filters (defaults or the user's cached tweaks)
        // in both modes; the toggle only narrows the visible rows to the
        // snapshot, which stays force-included server-side.
        setShowSelectedOnly((prev) => {
            setShowSelectedCaregiverIds(
                prev ? [] : selectedCaregiverIdsForRequest,
            );
            return !prev;
        });
        tableRef.current?.setPageIndex(0);
    }, [selectedCaregiverIdsForRequest, showSelectedOnly]);

    const displayCaregiverNamesLastFirst = useDisplayNamesLastFirst();
    const sortByLastName =
        organizationData?.features?.last_name_sorting_enabled === true;

    const recommendationRowsKey = useMemo(
        () =>
            (inhouseRecommendationsResponse?.recommendations ?? [])
                .map((recommendation) => recommendation.caregiver_id)
                .join(','),
        [inhouseRecommendationsResponse],
    );
    // Grouping is the order the list opens in, not a live reaction. Re-sorting
    // on a checkbox click moved the row the coordinator had just clicked out
    // from under them, onto another page once they were past the first one.
    // Suggestions are part of that order: unchecking a v2 suggestion strips it
    // from the outreach. Cached rows can arrive before the persisted selection
    // hydrates, so hydration is part of the key rather than only the row set.
    const groupingAnchorKey = `${hasHydratedSelection}:${recommendationRowsKey}`;
    const [grouping, setGrouping] = useState<{
        readonly anchorKey: string;
        readonly selectedCaregiverIds: ReadonlySet<string>;
        readonly suggestedCaregiverIds: ReadonlyArray<string>;
    }>(() => ({
        anchorKey: groupingAnchorKey,
        selectedCaregiverIds,
        suggestedCaregiverIds: preSelectedCaregiverIds ?? [],
    }));
    if (grouping.anchorKey !== groupingAnchorKey) {
        setGrouping({
            anchorKey: groupingAnchorKey,
            selectedCaregiverIds,
            suggestedCaregiverIds: preSelectedCaregiverIds ?? [],
        });
    }

    const recommendations = useMemo<Array<InhouseRecommendation>>(() => {
        if (!inhouseRecommendationsResponse?.recommendations) {
            return [];
        }
        let recs = [...inhouseRecommendationsResponse.recommendations];

        const query = debouncedSearchQuery.trim().toLocaleLowerCase();
        const formatDisplayName = (name: string) =>
            formatCaregiverName(name, displayCaregiverNamesLastFirst);
        recs = recs.filter((rec) =>
            matchesCaregiverNameSearch(rec, query, formatDisplayName),
        );

        // Filter to show only selected caregivers when toggle is active
        if (showSelectedOnly) {
            const selectedOnlyIds = new Set(showSelectedCaregiverIds);
            recs = recs.filter((rec) => selectedOnlyIds.has(rec.caregiver_id));
        }

        // For suggestion-backed outreaches, preserve the agent's suggestion
        // order as the baseline before table-level selection and column sorts.
        if (grouping.suggestedCaregiverIds.length > 0) {
            const suggestionOrder = new Map(
                grouping.suggestedCaregiverIds.map((id, i) => [id, i]),
            );
            recs.sort((a, b) => {
                const aSuggested = suggestionOrder.has(a.caregiver_id);
                const bSuggested = suggestionOrder.has(b.caregiver_id);

                // Suggested caregivers come first
                if (aSuggested !== bSuggested) {
                    return aSuggested ? -1 : 1;
                }

                // Within suggested: use agent suggestion order
                if (aSuggested && bSuggested) {
                    const aOrder =
                        suggestionOrder.get(a.caregiver_id) ?? Infinity;
                    const bOrder =
                        suggestionOrder.get(b.caregiver_id) ?? Infinity;
                    return aOrder - bOrder;
                }

                // Within non-suggested: use recommendation rank
                return a.rank - b.rank;
            });
        }

        // Fresh row objects so TanStack invalidates its cached priority
        // values when the grouping is re-anchored.
        return recs.map((rec) => ({
            ...rec,
            selection_priority: grouping.selectedCaregiverIds.has(
                rec.caregiver_id,
            )
                ? 1
                : 0,
        }));
    }, [
        debouncedSearchQuery,
        displayCaregiverNamesLastFirst,
        inhouseRecommendationsResponse,
        showSelectedOnly,
        showSelectedCaregiverIds,
        grouping,
    ]);

    // IDs of selected caregivers that only appear because they
    // were kept despite not matching filters (search/distance).
    const filteredOutSelectedIds = useMemo(() => {
        if (!inhouseRecommendationsResponse?.recommendations) {
            return new Set<string>();
        }
        const query = debouncedSearchQuery.trim().toLowerCase();
        const ids = new Set<string>();
        for (const rec of inhouseRecommendationsResponse.recommendations) {
            if (!selectedCaregiverIds.has(rec.caregiver_id)) {
                continue;
            }
            const matchesSearch =
                !query ||
                matchesCaregiverNameSearch(rec, query, (name: string) =>
                    formatCaregiverName(name, displayCaregiverNamesLastFirst),
                );
            const matchesDistance =
                maxDistanceMiles === null ||
                (rec.distance !== null &&
                    rec.distance !== undefined &&
                    rec.distance <= maxDistanceMiles);
            if (!matchesSearch || !matchesDistance) {
                ids.add(rec.caregiver_id);
            }
        }
        return ids;
    }, [
        inhouseRecommendationsResponse,
        debouncedSearchQuery,
        displayCaregiverNamesLastFirst,
        maxDistanceMiles,
        selectedCaregiverIds,
    ]);

    useEffect(() => {
        lastClickedCaregiverIdRef.current = null;
    }, [
        inhouseRecommendationsResponse?.recommendations,
        debouncedSearchQuery,
        maxDistanceMiles,
        showSelectedOnly,
    ]);

    useEffect(() => {
        // The user's own click is itself a selection change; only a change
        // from elsewhere invalidates where they started their range.
        if (hasPendingUserSelectionChangeRef.current) {
            hasPendingUserSelectionChangeRef.current = false;
            return;
        }
        lastClickedCaregiverIdRef.current = null;
    }, [selectedCaregiverIdsForRequest]);

    const preSelectedCaregiverOrderKey = JSON.stringify(
        preSelectedCaregiverIds ?? [],
    );
    useEffect(() => {
        lastClickedCaregiverIdRef.current = null;
    }, [preSelectedCaregiverOrderKey]);

    const isWellSkyOrganization =
        organizationData?.general_settings?.primary_data_source === 'wellsky';

    const skipListedCaregiverIds = useMemo(
        () =>
            new Set(
                recommendations
                    .filter(isOnSkipList)
                    .map((recommendation) => recommendation.caregiver_id),
            ),
        [recommendations],
    );
    const canSelectCaregiver = useCallback(
        (caregiverId: string) =>
            canNewlySelectCaregiver(
                caregiverId,
                disabledCaregiverIds,
                skipListedCaregiverIds,
            ),
        [disabledCaregiverIds, skipListedCaregiverIds],
    );

    const allEnabledCaregiverIds = useMemo(
        () =>
            recommendations
                .map((r) => r.caregiver_id)
                .filter(canSelectCaregiver),
        [recommendations, canSelectCaregiver],
    );

    // Handle shift+click range selection
    const handleCheckboxClick = useCallback(
        (caregiverId: string, shiftKey: boolean) => {
            if (isAgentRunning) {
                return;
            }
            // Pre-paginated so a range can span the page the user started on.
            const orderedCaregiverIds =
                tableRef.current
                    ?.getPrePaginatedRowModel()
                    .rows.map((row) => row.original.caregiver_id) ?? [];
            const lastClickedCaregiverId = lastClickedCaregiverIdRef.current;
            if (shiftKey && lastClickedCaregiverId) {
                const startIndex = orderedCaregiverIds.indexOf(
                    lastClickedCaregiverId,
                );
                const endIndex = orderedCaregiverIds.indexOf(caregiverId);

                if (startIndex !== -1 && endIndex !== -1) {
                    const rangeStart = Math.min(startIndex, endIndex);
                    const rangeEnd = Math.max(startIndex, endIndex);

                    // Get the selection state of the clicked row to determine if we're selecting or deselecting
                    const isSelecting = !selectedCaregiverIds.has(caregiverId);

                    setUserSelectedCaregiverIds((prev) => {
                        const newSet = new Set(prev);
                        for (let i = rangeStart; i <= rangeEnd; i++) {
                            const rangeCaregiverId = orderedCaregiverIds[i];
                            if (
                                rangeCaregiverId &&
                                canSelectCaregiver(rangeCaregiverId)
                            ) {
                                if (isSelecting) {
                                    newSet.add(rangeCaregiverId);
                                } else {
                                    newSet.delete(rangeCaregiverId);
                                }
                            }
                        }
                        return newSet;
                    });
                }
            }
            lastClickedCaregiverIdRef.current = caregiverId;
            hasPendingUserSelectionChangeRef.current = true;
        },
        [
            isAgentRunning,
            selectedCaregiverIds,
            canSelectCaregiver,
            setUserSelectedCaregiverIds,
        ],
    );
    const handleSortingChange = useCallback(() => {
        lastClickedCaregiverIdRef.current = null;
    }, []);

    const handleSuggestionFeedback = useCallback(
        async (
            caregiverId: string,
            feedback: SuggestionFeedback,
            comment?: string,
        ): Promise<boolean> => {
            setSuggestionFeedbackByCaregiverId((previous) => ({
                ...previous,
                [caregiverId]: feedback,
            }));
            setPersistedFeedbackByCaregiverState((previous) => ({
                ...previous,
                [caregiverId]: {
                    is_helpful: feedback === 'up',
                    feedback: comment ?? null,
                    caregiver_name:
                        previous[caregiverId]?.caregiver_name ?? null,
                    updated_at: new Date().toISOString(),
                },
            }));
            if (feedback === 'down') {
                setUserSelectedCaregiverIds((previous) => {
                    if (!previous.has(caregiverId)) {
                        return previous;
                    }
                    const next = new Set(previous);
                    next.delete(caregiverId);
                    return next;
                });
            }
            if (!outreachId || !organizationId) {
                return false;
            }
            try {
                await getApiClient().post({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/suggestion-feedback',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreachId,
                    },
                    body: {
                        caregiver_id: caregiverId,
                        is_helpful: feedback === 'up',
                        feedback: comment ?? null,
                    },
                    reason: 'inhouse-recommendations-suggestion-feedback',
                });
                return true;
            } catch (error) {
                return false;
            }
        },
        [outreachId, organizationId, setUserSelectedCaregiverIds],
    );

    const handleSuggestionFeedbackUndo = useCallback(
        (caregiverId: string) => {
            const feedback = suggestionFeedbackByCaregiverId[caregiverId];
            if (!feedback) {
                return;
            }
            setSuggestionFeedbackByCaregiverId((previous) => {
                if (!(caregiverId in previous)) {
                    return previous;
                }
                const next = { ...previous };
                delete next[caregiverId];
                return next;
            });
            setPersistedFeedbackByCaregiverState((previous) => {
                if (!(caregiverId in previous)) {
                    return previous;
                }
                const next = { ...previous };
                delete next[caregiverId];
                return next;
            });
            if (
                preSelectedCaregiverIds?.includes(caregiverId) &&
                canSelectCaregiver(caregiverId)
            ) {
                setUserSelectedCaregiverIds((previous) => {
                    if (previous.has(caregiverId)) {
                        return previous;
                    }
                    const next = new Set(previous);
                    next.add(caregiverId);
                    return next;
                });
            }
            if (outreachId && organizationId) {
                void getApiClient().post({
                    url: '/organizations/{organization_id}/callouts/{callout_id}/suggestion-feedback',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: outreachId,
                    },
                    body: {
                        caregiver_id: caregiverId,
                        is_helpful: feedback === 'up',
                        feedback: SUGGESTION_FEEDBACK_UNDO_TOKEN,
                    },
                    reason: 'inhouse-recommendations-suggestion-feedback-undo',
                });
            }
        },
        [
            canSelectCaregiver,
            organizationId,
            outreachId,
            preSelectedCaregiverIds,
            setUserSelectedCaregiverIds,
            suggestionFeedbackByCaregiverId,
        ],
    );

    const rowSelection = useMemo(() => {
        const selection: RowSelectionState = {};
        recommendations.forEach((rec, index) => {
            if (selectedCaregiverIds.has(rec.caregiver_id)) {
                selection[index.toString()] = true;
            }
        });
        return selection;
    }, [selectedCaregiverIds, recommendations]);
    const overtimeHoursLimit = hasOrgDefaults
        ? (organizationRecommendationDefaults?.max_weekly_hours ?? null)
        : 40;
    const areDefaultsApplied =
        areColumnFiltersEqual(activeColumnFilters, initialColumnFilters) &&
        !hasMaxDistanceOverride &&
        !hasContactAttemptsOverride;

    // v3 shows an Any-shift group's availability shift by shift, but only
    // once the response carries it. An API that predates the field (a
    // frontend-first deploy, a cached bundle) omits it, and the pills would
    // then report "nothing on file" for everyone in place of real coverage.
    const hasPerShiftAvailability = (
        groupQuery.data?.recommendations ?? []
    ).some(
        (recommendation) =>
            recommendation.availability_by_shift_id !== undefined,
    );
    const showShiftChips =
        uiV3 && groupMode === 'any' && hasPerShiftAvailability;
    const shiftChipShifts = showShiftChips
        ? groupQuery.data?.shifts
        : undefined;

    const columns = useMemo(() => {
        const allColumns = createInhouseRecommendationColumns(
            disabledCaregiverIds,
            skipListedCaregiverIds,
            selectedCaregiverIds,
            setUserSelectedCaregiverIds,
            allEnabledCaregiverIds,
            Boolean(shiftGroupId),
            (name: string) =>
                formatCaregiverName(name, displayCaregiverNamesLastFirst),
            sortByLastName,
            timezone,
            overtimeHoursLimit,
            shiftDurationMinutes,
            shiftStartTime,
            shiftEndTime,
            handleCheckboxClick,
            agentReasoningMap,
            suggestedCaregiverIdSet,
            suggestionFeedbackByCaregiverId,
            handleSuggestionFeedback,
            handleSuggestionFeedbackUndo,
            persistedFeedbackByCaregiverState,
            setSelectedCaregiverDetailId,
            patientTags,
            organizationId ?? undefined,
            true,
            isAgentRunning,
            selectionCoverage,
            uiV3,
        );

        const visibleColumns = allColumns.filter((column) => {
            if (!isWellSkyOrganization && column.id === 'star_rating') {
                return false;
            }
            if (
                !showEhrCaregiverStatus &&
                column.id === 'ehr_caregiver_status'
            ) {
                return false;
            }
            if (
                !showHHAExchangeCompliance &&
                column.id === 'caregiver_compliance'
            ) {
                return false;
            }
            return true;
        });
        if (!shiftChipShifts) {
            return visibleColumns;
        }

        // One chip per shift replaces both coverage counts. The two
        // availability columns stay defined, hidden by default, because the
        // availability filters and the default sort read them.
        const allShiftIds = new Set(
            shiftChipShifts.map((shift) => shift.shift_id),
        );
        const shiftChipsColumn: ColumnDef<InhouseRecommendation> = {
            id: 'shift_availability',
            header: 'Shifts',
            enableSorting: false,
            enableHiding: false,
            // Pinned like the coverage counts it replaces: a saved column
            // order would otherwise push a new column to the far right.
            meta: { pinnedLeft: true },
            cell: ({ row }) => {
                const caregiverId = row.original.caregiver_id;
                // Without per-shift staging, ticking a caregiver selects
                // them for every shift in the group.
                const selectedShiftIds =
                    selectionCoverage?.stagedShiftIdsByCaregiverId.get(
                        caregiverId,
                    ) ??
                    (selectedCaregiverIds.has(caregiverId)
                        ? allShiftIds
                        : NO_SHIFT_IDS);
                // Per-shift picks are server rows, which only v3-born
                // outreaches have; elsewhere the pills stay display only.
                const canToggleShifts =
                    isV3BornOutreach &&
                    !isAgentRunning &&
                    !disabledCaregiverIds.includes(caregiverId);
                return (
                    <ShiftAvailabilityChips
                        shifts={shiftChipShifts}
                        availabilityByShiftId={
                            row.original.availability_by_shift_id
                        }
                        selectedShiftIds={selectedShiftIds}
                        timezone={timezone}
                        onToggleShift={
                            canToggleShifts
                                ? (shiftId, selected) =>
                                      persistV3ShiftSelection({
                                          caregiverId,
                                          shiftId,
                                          selected,
                                      })
                                : undefined
                        }
                        canSelectMore={canNewlySelectCaregiver(
                            caregiverId,
                            disabledCaregiverIds,
                            skipListedCaregiverIds,
                        )}
                    />
                );
            },
        };
        return visibleColumns.flatMap((column) => {
            if (
                column.id === 'group_coverage' ||
                column.id === 'selection_coverage'
            ) {
                return [];
            }
            return column.id === 'caregiver_name'
                ? [column, shiftChipsColumn]
                : [column];
        });
    }, [
        disabledCaregiverIds,
        skipListedCaregiverIds,
        selectedCaregiverIds,
        allEnabledCaregiverIds,
        displayCaregiverNamesLastFirst,
        sortByLastName,
        timezone,
        overtimeHoursLimit,
        shiftDurationMinutes,
        shiftStartTime,
        shiftEndTime,
        handleCheckboxClick,
        agentReasoningMap,
        suggestedCaregiverIdSet,
        suggestionFeedbackByCaregiverId,
        handleSuggestionFeedback,
        handleSuggestionFeedbackUndo,
        persistedFeedbackByCaregiverState,
        patientTags,
        setUserSelectedCaregiverIds,
        shiftGroupId,
        showEhrCaregiverStatus,
        showHHAExchangeCompliance,
        isWellSkyOrganization,
        organizationId,
        isAgentRunning,
        selectionCoverage,
        uiV3,
        shiftChipShifts,
        isV3BornOutreach,
        persistV3ShiftSelection,
    ]);

    const renderMobileRecommendationCard = useCallback(
        (recommendation: InhouseRecommendation) => {
            const caregiverId = recommendation.caregiver_id;
            const isSelected = selectedCaregiverIds.has(caregiverId);
            const isSkipListed = skipListedCaregiverIds.has(caregiverId);
            const isDisabled =
                disabledCaregiverIds.includes(caregiverId) ||
                isAgentRunning ||
                (!isSelected && !canSelectCaregiver(caregiverId));
            const displayName = formatCaregiverName(
                recommendation.caregiver_name,
                displayCaregiverNamesLastFirst,
            );
            const availabilityPreference =
                getAvailabilityPreference(recommendation);
            const scheduleAvailability =
                getScheduleAvailability(recommendation);
            const phoneContacts = Array.from(
                new Map(
                    (recommendation.contacts ?? [])
                        .filter((contact) => contact.type === 'phone')
                        .map((contact) => [contact.address, contact]),
                ).values(),
            );
            const roles = recommendation.roles as Array<CaregiverRole> | null;
            const tags = recommendation.caregiver_tags ?? [];
            const isOvertime =
                recommendation.is_overtime === true ||
                (overtimeHoursLimit !== null &&
                    recommendation.weekly_hours_if_assigned !== null &&
                    recommendation.weekly_hours_if_assigned >
                        overtimeHoursLimit);
            const reasoning = agentReasoningMap?.get(caregiverId);
            const suggestionFeedback =
                suggestionFeedbackByCaregiverId[caregiverId] ?? null;
            const hasSuggestion = Boolean(reasoning);
            const isExpanded = expandedMobileRecommendationIds.has(caregiverId);
            const coverage = getGroupCoverageCounts(recommendation);
            const coverageReasonText =
                getGroupUncoveredReasonText(recommendation);

            return (
                <div
                    data-testid="mobile-recommendation-row"
                    className={cn(
                        'min-w-0 rounded-md px-2 py-1.5 text-xs leading-tight',
                        hasSuggestion &&
                            'bg-agent-action-wash dark:ring-agent-action-border ring-1 ring-purple-100',
                        isDisabled ? 'opacity-60' : '',
                    )}
                >
                    <div className="flex min-w-0 items-start gap-2">
                        <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            onCheckedChange={(value) => {
                                if (
                                    isDisabled ||
                                    (value === true &&
                                        !canSelectCaregiver(caregiverId))
                                ) {
                                    return;
                                }
                                setUserSelectedCaregiverIds((prev) => {
                                    const next = new Set(prev);
                                    if (value === true) {
                                        next.add(caregiverId);
                                    } else {
                                        next.delete(caregiverId);
                                    }
                                    return next;
                                });
                            }}
                            aria-label={`Select ${displayName}`}
                            onClick={(event) => event.stopPropagation()}
                            className={cn(
                                'mt-0.5 h-3.5 w-3.5 shrink-0',
                                isDisabled
                                    ? 'cursor-not-allowed'
                                    : 'cursor-pointer',
                            )}
                        />
                        <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex min-w-0 items-center gap-1.5">
                                <button
                                    type="button"
                                    className="min-w-0 flex-1 truncate text-left font-medium underline underline-offset-2"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setSelectedCaregiverDetailId(
                                            caregiverId,
                                        );
                                    }}
                                >
                                    {displayName}
                                </button>
                                {coverage ? (
                                    <Tooltip>
                                        <TooltipTrigger asChild={true}>
                                            <Badge
                                                variant="secondary"
                                                className={cn(
                                                    'h-4 rounded-sm px-1 text-[10px] leading-none',
                                                    coverage.uncovered === 0
                                                        ? 'text-green-700 dark:text-green-400'
                                                        : 'text-amber-700 dark:text-amber-400',
                                                )}
                                            >
                                                {coverage.covered}/
                                                {coverage.total}
                                            </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {coverageReasonText ??
                                                `Covers ${coverage.covered}/${coverage.total}`}
                                        </TooltipContent>
                                    </Tooltip>
                                ) : null}
                                {!recommendation.currently_available &&
                                recommendation.unavailable_until ? (
                                    <Badge
                                        variant="secondary"
                                        className="h-4 rounded-sm px-1 text-[10px] leading-none"
                                    >
                                        On shift
                                    </Badge>
                                ) : null}
                                {isSkipListed ? (
                                    <SkipListBadge className="h-4 rounded-sm px-1 text-[10px] leading-none" />
                                ) : (recommendation.filter_reasons?.length ??
                                      0) > 0 ? (
                                    <Tooltip>
                                        <TooltipTrigger asChild={true}>
                                            <Badge
                                                variant="secondary"
                                                className="h-4 rounded-sm px-1 text-[10px] leading-none text-amber-700 dark:text-amber-400"
                                            >
                                                Outside filters
                                            </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            Would be excluded:{' '}
                                            {(
                                                recommendation.filter_reasons ??
                                                []
                                            )
                                                .map(formatFilterReason)
                                                .join(', ')}
                                        </TooltipContent>
                                    </Tooltip>
                                ) : null}
                                {reasoning && (
                                    <SuggestedReasoningBadge
                                        caregiverId={caregiverId}
                                        feedback={suggestionFeedback}
                                        onFeedback={handleSuggestionFeedback}
                                        onUndoFeedback={
                                            handleSuggestionFeedbackUndo
                                        }
                                        organizationId={
                                            organizationId ?? undefined
                                        }
                                        persistedFeedback={
                                            persistedFeedbackByCaregiverState[
                                                caregiverId
                                            ] ?? null
                                        }
                                        reasoning={reasoning}
                                    />
                                )}
                            </div>
                            {showEhrCaregiverStatus ||
                            showHHAExchangeCompliance ? (
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                    {showEhrCaregiverStatus ? (
                                        <div className="flex items-center gap-1">
                                            <span className="text-muted-foreground text-[10px] font-medium uppercase">
                                                Status
                                            </span>
                                            {recommendation.ehr_caregiver_status ? (
                                                <EhrCaregiverStatusBadge
                                                    status={
                                                        recommendation.ehr_caregiver_status
                                                    }
                                                    className="h-4 rounded-sm px-1 text-[10px] leading-none"
                                                />
                                            ) : (
                                                <span>—</span>
                                            )}
                                        </div>
                                    ) : null}
                                    {showHHAExchangeCompliance ? (
                                        <div className="flex items-center gap-1">
                                            <span className="text-muted-foreground text-[10px] font-medium uppercase">
                                                Compliance
                                            </span>
                                            {recommendation.caregiver_compliance ? (
                                                <CaregiverComplianceBadge
                                                    compliance={
                                                        recommendation.caregiver_compliance
                                                    }
                                                    className="h-4 max-w-32 rounded-sm px-1 text-[10px] leading-none"
                                                />
                                            ) : (
                                                <span>—</span>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] leading-4">
                                <span className="whitespace-nowrap">
                                    Pref{' '}
                                    {formatRecommendationDefaultOptionLabel(
                                        'availability_preference',
                                        availabilityPreference,
                                    )}
                                </span>
                                <span className="whitespace-nowrap">
                                    Sched{' '}
                                    {formatRecommendationDefaultOptionLabel(
                                        'schedule_availability',
                                        scheduleAvailability,
                                    )}
                                </span>
                                <span className="whitespace-nowrap">
                                    {formatDistance(recommendation.distance)}
                                </span>
                                <span className="whitespace-nowrap">
                                    Visits{' '}
                                    {recommendation.previous_visits ?? '—'}
                                </span>
                                <span className="whitespace-nowrap">
                                    Hrs {recommendation.weekly_hours ?? '—'}
                                    {recommendation.weekly_hours_if_assigned !==
                                    null
                                        ? ` -> ${recommendation.weekly_hours_if_assigned}`
                                        : ''}
                                </span>
                                {recommendation.office_name ? (
                                    <span className="max-w-full truncate">
                                        {recommendation.office_name}
                                    </span>
                                ) : null}
                                {recommendation.postal_code ? (
                                    <span className="whitespace-nowrap">
                                        {recommendation.postal_code}
                                    </span>
                                ) : null}
                                {isOvertime ? (
                                    <span className="whitespace-nowrap text-amber-600">
                                        Overtime
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 shrink-0 p-0"
                            aria-label={
                                isExpanded
                                    ? `Hide details for ${displayName}`
                                    : `Show details for ${displayName}`
                            }
                            aria-expanded={isExpanded}
                            onClick={(event) => {
                                event.stopPropagation();
                                setExpandedMobileRecommendationIds(
                                    (previous) => {
                                        const next = new Set(previous);
                                        if (next.has(caregiverId)) {
                                            next.delete(caregiverId);
                                        } else {
                                            next.add(caregiverId);
                                        }
                                        return next;
                                    },
                                );
                            }}
                        >
                            <ChevronDown
                                className={cn(
                                    'h-4 w-4 transition-transform',
                                    isExpanded ? 'rotate-180' : '',
                                )}
                            />
                        </Button>
                    </div>
                    {isExpanded ? (
                        <div
                            className={cn(
                                'text-muted-foreground mt-2 space-y-2 border-t pt-2',
                                hasSuggestion
                                    ? 'dark:border-agent-action-border border-purple-200'
                                    : 'border-border',
                            )}
                        >
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                                <span>
                                    <span className="text-foreground font-medium">
                                        Preferred:
                                    </span>{' '}
                                    {formatRecommendationDefaultOptionLabel(
                                        'availability_preference',
                                        availabilityPreference,
                                    )}
                                </span>
                                <span>
                                    <span className="text-foreground font-medium">
                                        Schedule:
                                    </span>{' '}
                                    {formatRecommendationDefaultOptionLabel(
                                        'schedule_availability',
                                        scheduleAvailability,
                                    )}
                                </span>
                                <span>
                                    <span className="text-foreground font-medium">
                                        Office:
                                    </span>{' '}
                                    {recommendation.office_name || '—'}
                                </span>
                                <span>
                                    <span className="text-foreground font-medium">
                                        Zip:
                                    </span>{' '}
                                    {recommendation.postal_code ?? '—'}
                                </span>
                                <span>
                                    <span className="text-foreground font-medium">
                                        Distance:
                                    </span>{' '}
                                    {formatDistance(recommendation.distance)}
                                </span>
                                <span>
                                    <span className="text-foreground font-medium">
                                        Visits:
                                    </span>{' '}
                                    {recommendation.previous_visits ?? '—'}
                                </span>
                                <span className="col-span-2">
                                    <span className="text-foreground font-medium">
                                        Weekly:
                                    </span>{' '}
                                    {recommendation.weekly_hours ?? '—'}
                                    {recommendation.weekly_hours_if_assigned !==
                                    null
                                        ? ` -> ${recommendation.weekly_hours_if_assigned}`
                                        : ''}
                                    {isOvertime ? (
                                        <span className="ml-2 text-amber-600">
                                            Overtime
                                        </span>
                                    ) : null}
                                </span>
                            </div>
                            {roles && roles.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                    {roles.map((role) => (
                                        <RoleBadge
                                            key={role}
                                            role={role}
                                            className="text-[10px]"
                                        />
                                    ))}
                                </div>
                            ) : null}
                            {tags.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                    {tags.map((tag) => (
                                        <Badge
                                            key={tag}
                                            variant="secondary"
                                            className="text-[10px]"
                                        >
                                            {tag}
                                        </Badge>
                                    ))}
                                </div>
                            ) : null}
                            <div className="flex flex-wrap gap-1">
                                {phoneContacts.length > 0 ? (
                                    phoneContacts.map((contact, index) => (
                                        <ContactBadge
                                            key={`${contact.address}-${index}`}
                                            type="phone"
                                            address={contact.address}
                                        />
                                    ))
                                ) : (
                                    <span className="text-muted-foreground text-[11px]">
                                        No phone
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            );
        },
        [
            agentReasoningMap,
            canSelectCaregiver,
            disabledCaregiverIds,
            displayCaregiverNamesLastFirst,
            expandedMobileRecommendationIds,
            handleSuggestionFeedback,
            handleSuggestionFeedbackUndo,
            isAgentRunning,
            showEhrCaregiverStatus,
            showHHAExchangeCompliance,
            organizationId,
            overtimeHoursLimit,
            persistedFeedbackByCaregiverState,
            selectedCaregiverIds,
            setUserSelectedCaregiverIds,
            skipListedCaregiverIds,
            suggestionFeedbackByCaregiverId,
        ],
    );

    const selectedCaregiverCount = selectedCaregiverIds.size;
    // Org-configurable cap, enforced server-side in
    // libraries/python/outreach_operations/start_outreach.py. 300 mirrors
    // DEFAULT_MAX_CAREGIVERS_PER_OUTREACH there.
    const maxOutreachCaregivers =
        organizationData?.callout_settings?.max_caregivers_per_outreach ?? 300;
    const isOverCaregiverLimit = selectedCaregiverCount > maxOutreachCaregivers;
    const shouldShowToolbarShowSelectedButton =
        recommendations.length > 0 || selectedCaregiverCount > 0;
    // Only mask the table while there is nothing to show. Once recommendations
    // are rendered, drop the overlay even if the agent is still flagged as
    // running — otherwise it lingers until the next outreach poll flips the
    // flag, long after the rows are visible.
    const showAgentGeneratingMask =
        isAgentRunning && recommendations.length === 0;

    // A rejected filter keeps the toolbar mounted so the filter that caused it
    // can be removed; filters persist per outreach, so replacing the whole
    // table would reload the same rejected filter with no way back.
    const filterError =
        error && isRecommendationFilterTooLarge(error) ? error : undefined;

    if (error && !filterError) {
        return (
            <div className="text-destructive flex justify-center p-6">
                Error loading recommendations: {error.message}
            </div>
        );
    }

    const shouldConstrainTableHeight = isMobile;
    // v3 desktop puts search on the same row as Filter and Columns; mobile
    // keeps it above the horizontally scrolling toolbar strip.
    const isSearchInToolbar = uiV3 && !isMobile;
    const searchInput = (
        <SearchInput
            containerClassName={
                isSearchInToolbar
                    ? undefined
                    : 'min-w-0 flex-1 md:min-w-[16rem]'
            }
            placeholder="Search by caregiver name…"
            value={searchQuery}
            onValueChange={handleSearchQueryChange}
            disabled={isRecommendationDefaultsLoading || isAgentRunning}
        />
    );

    return (
        <div
            className={cn(
                'flex min-h-0 flex-col max-md:h-full max-md:max-h-full max-md:overflow-hidden',
                shouldConstrainTableHeight
                    ? 'md:h-full md:max-h-full md:flex-1 md:overflow-hidden'
                    : 'md:overflow-visible',
            )}
        >
            <div className="flex flex-shrink-0 items-center justify-between gap-2">
                <h2
                    className={cn(
                        'hidden text-base font-medium md:block',
                        uiV3 ? 'md:text-sm' : 'md:text-lg',
                    )}
                >
                    {title}
                </h2>
                <div className="flex min-w-0 items-center gap-2 md:ml-auto">
                    {organizationId &&
                    recalcShiftIds.length > 0 &&
                    (inhouseRecommendationsResponse?.recommendations?.length ??
                        0) > 0 ? (
                        <RecalcShiftDistancesButton
                            organizationId={organizationId}
                            shiftIds={recalcShiftIds}
                            recommendedCaregiverIds={(
                                inhouseRecommendationsResponse?.recommendations ??
                                []
                            ).map((rec) => rec.caregiver_id)}
                            onSuccess={() => {
                                void (
                                    shiftGroupId ? groupQuery : singleShiftQuery
                                ).mutate();
                            }}
                        />
                    ) : null}
                    <Badge variant="secondary">
                        {isLoading || isRecommendationDefaultsLoading
                            ? 'Loading...'
                            : `${recommendations.length} Caregivers`}
                    </Badge>
                </div>
                {mobileHeaderAction ? (
                    <div className="shrink-0 md:hidden">
                        {mobileHeaderAction}
                    </div>
                ) : null}
            </div>
            <div
                className={cn(
                    'flex flex-shrink-0 flex-wrap items-end gap-2 py-1 md:gap-3 md:py-4',
                    isSearchInToolbar && 'md:py-0',
                )}
            >
                {isSearchInToolbar ? null : searchInput}
                {isSearchBypassingFilters ? (
                    <span className="text-muted-foreground text-xs">
                        Searching all caregivers — filters paused
                    </span>
                ) : null}
            </div>
            {filterError ? (
                <div
                    data-testid="recommendation-filter-error"
                    className="border-destructive/40 bg-destructive/5 text-destructive flex flex-shrink-0 flex-wrap items-center gap-2 rounded-md border px-2.5 py-2 text-xs"
                >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>{recommendationErrorMessage(filterError)}</span>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={handleResetToOrgDefaults}
                    >
                        Reset filters
                    </Button>
                </div>
            ) : null}
            {suggestedRelaxationLabels.length > 0 ? (
                <div
                    data-testid="filter-relaxation-suggestions"
                    className="border-border bg-muted/40 text-muted-foreground flex flex-shrink-0 flex-wrap items-center gap-1.5 rounded-md border px-2.5 py-2 text-xs"
                >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span>No matches. Phoebe suggests relaxing:</span>
                    {suggestedRelaxationLabels.map((label) => (
                        <Badge
                            key={label}
                            variant="secondary"
                            className="h-5 px-1.5 text-[11px]"
                        >
                            {label}
                        </Badge>
                    ))}
                </div>
            ) : null}
            <div
                className={cn(
                    'min-h-0 max-md:overflow-hidden',
                    shouldConstrainTableHeight ? 'flex-1' : '',
                )}
            >
                <div
                    className={cn(
                        'flex flex-col max-md:h-full max-md:max-h-full max-md:overflow-hidden',
                        shouldConstrainTableHeight
                            ? 'md:h-full md:max-h-full md:min-h-0'
                            : '',
                    )}
                >
                    <div
                        className={cn(
                            'relative flex flex-col max-md:h-full max-md:max-h-full max-md:overflow-hidden',
                            shouldConstrainTableHeight
                                ? 'md:h-full md:max-h-full md:min-h-0'
                                : '',
                        )}
                    >
                        <div
                            data-testid="inhouse-recommendations-scroll-region"
                            className={cn(
                                'relative min-h-0 max-md:overflow-hidden [&>div]:rounded-none [&>div]:border-x-0',
                                // Purple means Phoebe acted; a coordinator's
                                // own pick is a neutral grey under v3.
                                uiV3
                                    ? '[&_tr[data-state=selected]]:bg-muted [&>div]:border-t-0'
                                    : '[&_tr[data-state=selected]]:bg-agent-action-wash',
                                shouldConstrainTableHeight ? 'flex-1' : '',
                                showAgentGeneratingMask && 'opacity-50',
                            )}
                        >
                            {showAgentGeneratingMask && (
                                <Fragment>
                                    <div className="animate-shimmer pointer-events-none absolute inset-0 z-10 bg-black/10 dark:bg-white/10" />
                                    <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
                                        <div className="bg-primary text-primary-foreground flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold shadow-lg ring-2 ring-white/40 dark:ring-black/40">
                                            <svg
                                                className="h-4 w-4 animate-spin"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                />
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                />
                                            </svg>
                                            Generating recommendations…
                                        </div>
                                    </div>
                                </Fragment>
                            )}
                            <DataTable
                                // Column defaults are read once at mount, and
                                // the v3 flag can resolve after it.
                                key={`${dataTableKey}:${showShiftChips}`}
                                className={cn(
                                    'max-md:flex max-md:h-full max-md:max-h-full max-md:min-h-0 max-md:flex-col max-md:overflow-hidden',
                                    shouldConstrainTableHeight
                                        ? 'md:flex md:h-full md:max-h-full md:min-h-0 md:flex-col'
                                        : 'md:block',
                                )}
                                columns={columns}
                                data={recommendations}
                                columnVisibilityKey={COLUMN_VISIBILITY_KEY}
                                columnOrderKey={COLUMN_ORDER_KEY}
                                enableRowSelection={true}
                                rowSelection={rowSelection}
                                preserveSelectedRows={true}
                                // v2 filter persistence is parent-owned
                                // (write-on-user-change): DataTable's storage
                                // hook stamps its current value on mount,
                                // which would record [] before the entity
                                // defaults load and pre-empt them.
                                filterKeyPrefix={undefined}
                                initialColumnFilters={
                                    dataTableInitialColumnFilters
                                }
                                onColumnFiltersChange={
                                    handleColumnFiltersChange
                                }
                                manualFiltering={true}
                                isLoading={
                                    // Block-render until dismissals are
                                    // known (checkbox state would be wrong)
                                    // or while there is no response at all.
                                    // Revalidations (e.g. a dismissal
                                    // changing always-include ids) keep the
                                    // previous rows in place instead of
                                    // swapping in the loading row, which
                                    // collapses the container and jumps the
                                    // scroll position back to the top.
                                    !hasLoadedDismissedCaregivers ||
                                    (isLoading &&
                                        inhouseRecommendationsResponse ===
                                            undefined) ||
                                    isRecommendationDefaultsLoading
                                }
                                getRowClassName={(row) =>
                                    cn(
                                        disabledCaregiverIds.includes(
                                            row.caregiver_id,
                                        )
                                            ? 'opacity-60'
                                            : '',
                                        // A pick outside the current filters.
                                        // v3 keeps it at full strength (a
                                        // coordinator's pick should not look
                                        // disabled) and marks it in italics.
                                        filteredOutSelectedIds.has(
                                            row.caregiver_id,
                                        )
                                            ? uiV3
                                                ? 'italic'
                                                : 'italic opacity-60'
                                            : '',
                                        dismissedCaregiverIds.has(
                                            row.caregiver_id,
                                        ) ||
                                            skipListedCaregiverIds.has(
                                                row.caregiver_id,
                                            )
                                            ? 'opacity-60'
                                            : '',
                                        agentReasoningMap?.has(row.caregiver_id)
                                            ? 'bg-agent-action-wash md:bg-transparent'
                                            : '',
                                    )
                                }
                                // Caregivers who have worked with this client
                                // before open at the top, most visits first —
                                // familiarity is the strongest signal a
                                // coordinator picks on. Schedule availability
                                // breaks ties so caregivers without conflicts
                                // still come first among equal visit counts,
                                // and the stable sort keeps the server's
                                // recommendation ranking below both. Clicking
                                // any column header replaces this default.
                                initialSorting={INITIAL_RECOMMENDATION_SORTING}
                                fixedSorting={SELECTED_FIRST_SORTING}
                                onSortingChange={handleSortingChange}
                                initialColumnVisibility={{
                                    selection_priority: false,
                                    contacts: false,
                                    gender: false,
                                    primary_language: false,
                                    roles: false,
                                    postal_code: false,
                                    star_rating: false,
                                    // The shift chips say the same thing
                                    // per shift; these stay available in
                                    // the Columns menu.
                                    ...(showShiftChips
                                        ? {
                                              availability_preference: false,
                                              schedule_availability: false,
                                          }
                                        : {}),
                                }}
                                pageSize={
                                    showSelectedOnly
                                        ? SHOW_SELECTED_PAGE_SIZE
                                        : pageSize
                                }
                                pageSizeOptions={
                                    ELIGIBLE_CAREGIVERS_PAGE_SIZE_OPTIONS
                                }
                                hidePagination={showSelectedOnly}
                                paginationPlacement={
                                    isMobile ? 'rows' : 'outside'
                                }
                                onPageSizeChange={(nextPageSize) =>
                                    setPageSize(nextPageSize)
                                }
                                tableRef={tableRef}
                                // DataTable swaps to cards on its own width,
                                // so a docked chat triggers them on desktop.
                                // v3 keeps the table there; phones keep cards.
                                renderMobileCard={
                                    uiV3 && !isMobile
                                        ? undefined
                                        : renderMobileRecommendationCard
                                }
                                mobileRowsClassName={cn(
                                    // On md+ the card list only appears when
                                    // the panel is squeezed; the desktop
                                    // wrappers don't bound its height, so cap
                                    // it to keep scrolling internal.
                                    'mt-4 min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] md:max-h-[28rem] [&_li]:px-2 [&_li]:py-2',
                                )}
                                tableRowsClassName={cn(
                                    'max-md:min-h-0 max-md:flex-1 max-md:overflow-y-auto max-md:overscroll-contain max-md:[-webkit-overflow-scrolling:touch] md:min-h-0 md:flex-1 md:overflow-y-auto md:[&_thead]:sticky md:[&_thead]:top-0 md:[&_thead]:z-20 md:[&_thead]:bg-background md:[&_th]:bg-background',
                                )}
                                toolbarClassName="shrink-0 px-1 pt-2.5 pb-1.5 md:p-2"
                                renderToolbar={(table) => {
                                    const selectedSegmentLabel = `Selected (${selectedCaregiverCount})`;
                                    const showSelectedButton =
                                        shouldShowToolbarShowSelectedButton ? (
                                            <Fragment>
                                                <SegmentedControl
                                                    options={[
                                                        {
                                                            value: 'selected',
                                                            label: ({
                                                                selected,
                                                            }) => (
                                                                <span
                                                                    className={cn(
                                                                        selected &&
                                                                            !uiV3 &&
                                                                            'text-purple-700 dark:text-violet-300',
                                                                    )}
                                                                >
                                                                    {
                                                                        selectedSegmentLabel
                                                                    }
                                                                </span>
                                                            ),
                                                            ariaLabel:
                                                                selectedSegmentLabel,
                                                        },
                                                        {
                                                            value: 'all',
                                                            label: 'All',
                                                        },
                                                    ]}
                                                    value={
                                                        showSelectedOnly
                                                            ? 'selected'
                                                            : 'all'
                                                    }
                                                    onValueChange={(next) => {
                                                        if (
                                                            (next ===
                                                                'selected') !==
                                                            showSelectedOnly
                                                        ) {
                                                            toggleShowSelectedOnly();
                                                        }
                                                    }}
                                                    disabled={
                                                        selectedCaregiverCount ===
                                                            0 &&
                                                        !showSelectedOnly
                                                    }
                                                    layout="auto"
                                                    size="xs"
                                                />
                                                {isOverCaregiverLimit && (
                                                    <span className="text-destructive text-sm">
                                                        Maximum{' '}
                                                        {maxOutreachCaregivers}{' '}
                                                        caregivers allowed
                                                    </span>
                                                )}
                                            </Fragment>
                                        ) : undefined;
                                    const scheduleAvailabilityFilterValue =
                                        table
                                            .getColumn('schedule_availability')
                                            ?.getFilterValue() as
                                            | {
                                                  include?: Array<string>;
                                              }
                                            | undefined;
                                    const scheduleAvailabilityValue =
                                        scheduleAvailabilityFilterValue
                                            ?.include?.[0] ?? 'all';
                                    const overtimeStatusFilterValue = table
                                        .getColumn('overtime_status')
                                        ?.getFilterValue() as
                                        | {
                                              include?: Array<string>;
                                          }
                                        | undefined;
                                    const overtimeStatusValue =
                                        overtimeStatusFilterValue
                                            ?.include?.[0] ?? 'all';
                                    const genderFilterValue = table
                                        .getColumn('gender')
                                        ?.getFilterValue() as
                                        | {
                                              include?: Array<string>;
                                          }
                                        | undefined;
                                    const genderValue =
                                        genderFilterValue?.include?.[0] ??
                                        'all';
                                    const starRatingFilterValue =
                                        normalizeStarRatingComparisonFilterValue(
                                            isWellSkyOrganization
                                                ? table
                                                      .getColumn('star_rating')
                                                      ?.getFilterValue()
                                                : undefined,
                                        );
                                    const starRatingFilterLabel =
                                        formatStarRatingComparisonFilterLabel(
                                            starRatingFilterValue,
                                        );
                                    const starRatingSelectedCount =
                                        starRatingFilterLabel ? 1 : 0;
                                    const starRatingOperatorLabel =
                                        starRatingFilterValue.operator
                                            ? formatStarRatingComparisonOperatorLabel(
                                                  starRatingFilterValue.operator,
                                              )
                                            : null;
                                    const starRatingValueLabel =
                                        starRatingFilterValue.value !==
                                        undefined
                                            ? String(
                                                  starRatingFilterValue.value,
                                              )
                                            : null;
                                    const capitalize = (s: string) =>
                                        s.charAt(0).toUpperCase() + s.slice(1);
                                    const genderLabel =
                                        genderValue === 'all'
                                            ? 'All'
                                            : capitalize(genderValue);
                                    const scheduleAvailabilityLabel =
                                        scheduleAvailabilityValue === 'all'
                                            ? 'All'
                                            : formatRecommendationDefaultOptionLabel(
                                                  'schedule_availability',
                                                  scheduleAvailabilityValue,
                                              );
                                    const overtimeStatusLabel =
                                        overtimeStatusValue === 'all'
                                            ? 'All'
                                            : formatRecommendationDefaultOptionLabel(
                                                  'overtime_status',
                                                  overtimeStatusValue,
                                              );
                                    const setRadioColumnFilterValue = (
                                        columnId:
                                            | 'schedule_availability'
                                            | 'overtime_status'
                                            | 'gender',
                                        value: string | undefined,
                                    ) => {
                                        table
                                            .getColumn(columnId)
                                            ?.setFilterValue(
                                                value === 'all'
                                                    ? undefined
                                                    : {
                                                          include: [value],
                                                          exclude: [],
                                                      },
                                            );
                                    };
                                    const clearRadioColumnFilter = (
                                        columnId:
                                            | 'schedule_availability'
                                            | 'overtime_status'
                                            | 'gender',
                                    ) => {
                                        table
                                            .getColumn(columnId)
                                            ?.setFilterValue(undefined);
                                    };
                                    const applyStarRatingFilter = (
                                        value:
                                            | StarRatingComparisonFilterValue
                                            | undefined,
                                    ) => {
                                        table
                                            .getColumn('star_rating')
                                            ?.setFilterValue(
                                                value?.operator === undefined ||
                                                    value.value === undefined
                                                    ? undefined
                                                    : value,
                                            );
                                    };
                                    const clearStarRatingFilter = () => {
                                        table
                                            .getColumn('star_rating')
                                            ?.setFilterValue(undefined);
                                    };
                                    const customFilters: Array<CustomFilterConfig> =
                                        [
                                            {
                                                id: 'distance',
                                                title: 'Distance',
                                                icon: MapPin,
                                                operatorLabel: '',
                                                selectedCount:
                                                    maxDistanceMiles !== null
                                                        ? 1
                                                        : 0,
                                                renderValueSelection: ({
                                                    open,
                                                    onOpenChange,
                                                }) => (
                                                    <Popover
                                                        open={open}
                                                        onOpenChange={
                                                            onOpenChange
                                                        }
                                                    >
                                                        <PopoverTrigger
                                                            asChild={true}
                                                        >
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-7 max-w-[16rem] min-w-[6rem] truncate rounded-full px-3 text-xs"
                                                            >
                                                                {maxDistanceMiles !==
                                                                null
                                                                    ? `Within ${maxDistanceMiles} mi`
                                                                    : 'Any distance'}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent
                                                            className="w-56 p-3"
                                                            align="start"
                                                        >
                                                            <div className="space-y-2">
                                                                <label className="text-xs font-medium">
                                                                    Within miles
                                                                </label>
                                                                <Input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.1"
                                                                    inputMode="decimal"
                                                                    placeholder="Max miles"
                                                                    value={
                                                                        maxDistanceMilesInput
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        setHasMaxDistanceOverride(
                                                                            true,
                                                                        );
                                                                        handleMaxDistanceMilesInputChange(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        );
                                                                    }}
                                                                    onBlur={() => {
                                                                        if (
                                                                            maxDistanceMiles !==
                                                                                null &&
                                                                            maxDistanceMiles !==
                                                                                committedMaxDistanceMiles
                                                                        ) {
                                                                            setCommittedMaxDistanceMiles(
                                                                                maxDistanceMiles,
                                                                            );
                                                                        }
                                                                    }}
                                                                    onKeyDown={(
                                                                        e,
                                                                    ) => {
                                                                        if (
                                                                            e.key ===
                                                                            'Enter'
                                                                        ) {
                                                                            setCommittedMaxDistanceMiles(
                                                                                maxDistanceMiles,
                                                                            );
                                                                        }
                                                                    }}
                                                                    className="h-8"
                                                                />
                                                            </div>
                                                        </PopoverContent>
                                                    </Popover>
                                                ),
                                                children: (
                                                    <div className="p-2">
                                                        <label className="text-xs font-medium">
                                                            Within miles
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            step="0.1"
                                                            inputMode="decimal"
                                                            placeholder="Max miles"
                                                            value={
                                                                maxDistanceMilesInput
                                                            }
                                                            onChange={(e) => {
                                                                setHasMaxDistanceOverride(
                                                                    true,
                                                                );
                                                                handleMaxDistanceMilesInputChange(
                                                                    e.target
                                                                        .value,
                                                                );
                                                            }}
                                                            onBlur={() =>
                                                                setCommittedMaxDistanceMiles(
                                                                    maxDistanceMiles,
                                                                )
                                                            }
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key ===
                                                                    'Enter'
                                                                ) {
                                                                    setCommittedMaxDistanceMiles(
                                                                        maxDistanceMiles,
                                                                    );
                                                                }
                                                            }}
                                                            className="mt-1 h-8"
                                                        />
                                                    </div>
                                                ),
                                                activeFilterLabels:
                                                    maxDistanceMiles !== null
                                                        ? [
                                                              `Within ${maxDistanceMiles} mi`,
                                                          ]
                                                        : [],
                                                onClear: () => {
                                                    setHasMaxDistanceOverride(
                                                        true,
                                                    );
                                                    setMaxDistanceMiles(null);
                                                    setCommittedMaxDistanceMiles(
                                                        null,
                                                    );
                                                },
                                            },
                                            {
                                                id: 'contact_attempts_48h',
                                                title: 'Contact Attempts',
                                                icon: Phone,
                                                operatorLabel: '',
                                                selectedCount:
                                                    effectiveMaxContactAttempts48h !==
                                                    null
                                                        ? 1
                                                        : 0,
                                                renderValueSelection: ({
                                                    open,
                                                    onOpenChange,
                                                }) => (
                                                    <Popover
                                                        open={open}
                                                        onOpenChange={
                                                            onOpenChange
                                                        }
                                                    >
                                                        <PopoverTrigger
                                                            asChild={true}
                                                        >
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-7 max-w-[16rem] min-w-[6rem] truncate rounded-full px-3 text-xs"
                                                            >
                                                                {effectiveMaxContactAttempts48h !==
                                                                null
                                                                    ? `< ${effectiveMaxContactAttempts48h} attempts`
                                                                    : 'Any attempts'}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent
                                                            className="w-56 p-3"
                                                            align="start"
                                                        >
                                                            <div className="space-y-2">
                                                                <label className="text-xs font-medium">
                                                                    Max attempts
                                                                    (48h)
                                                                </label>
                                                                <Input
                                                                    type="number"
                                                                    min="1"
                                                                    step="1"
                                                                    inputMode="numeric"
                                                                    placeholder="Exclude with X+ attempts"
                                                                    value={
                                                                        maxContactAttempts48hInput
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        setHasContactAttemptsOverride(
                                                                            true,
                                                                        );
                                                                        setMaxContactAttempts48hInput(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        );
                                                                    }}
                                                                    className="h-8"
                                                                />
                                                            </div>
                                                        </PopoverContent>
                                                    </Popover>
                                                ),
                                                children: (
                                                    <div className="p-2">
                                                        <label className="text-xs font-medium">
                                                            Max attempts (48h)
                                                        </label>
                                                        <Input
                                                            type="number"
                                                            min="1"
                                                            step="1"
                                                            inputMode="numeric"
                                                            placeholder="Exclude with X+ attempts"
                                                            value={
                                                                maxContactAttempts48hInput
                                                            }
                                                            onChange={(e) => {
                                                                setHasContactAttemptsOverride(
                                                                    true,
                                                                );
                                                                setMaxContactAttempts48hInput(
                                                                    e.target
                                                                        .value,
                                                                );
                                                            }}
                                                            className="mt-1 h-8"
                                                        />
                                                    </div>
                                                ),
                                                activeFilterLabels:
                                                    effectiveMaxContactAttempts48h !==
                                                    null
                                                        ? [
                                                              `< ${effectiveMaxContactAttempts48h} attempts`,
                                                          ]
                                                        : [],
                                                onClear: () => {
                                                    setHasContactAttemptsOverride(
                                                        true,
                                                    );
                                                    setMaxContactAttempts48hInput(
                                                        '',
                                                    );
                                                },
                                            },
                                            ...(isWellSkyOrganization
                                                ? [
                                                      {
                                                          id: 'star_rating',
                                                          title: 'Rating',
                                                          operatorLabel: '',
                                                          selectedCount:
                                                              starRatingSelectedCount,
                                                          renderValueSelection:
                                                              ({
                                                                  open,
                                                                  onOpenChange,
                                                              }: {
                                                                  open: boolean;
                                                                  onOpenChange: (
                                                                      open: boolean,
                                                                  ) => void;
                                                              }) => (
                                                                  <div className="flex items-center gap-1">
                                                                      {starRatingFilterValue.operator &&
                                                                      starRatingValueLabel ? (
                                                                          <DropdownMenu>
                                                                              <DropdownMenuTrigger
                                                                                  asChild={
                                                                                      true
                                                                                  }
                                                                              >
                                                                                  <Button
                                                                                      variant="outline"
                                                                                      size="sm"
                                                                                      className="h-7 items-center gap-1 rounded-full border-blue-500 px-3 text-xs text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/50"
                                                                                  >
                                                                                      <span className="truncate">
                                                                                          {
                                                                                              starRatingOperatorLabel
                                                                                          }
                                                                                      </span>
                                                                                      <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                                                                                  </Button>
                                                                              </DropdownMenuTrigger>
                                                                              <DropdownMenuContent align="start">
                                                                                  <DropdownMenuRadioGroup
                                                                                      value={
                                                                                          starRatingFilterValue.operator
                                                                                      }
                                                                                      onValueChange={(
                                                                                          nextValue,
                                                                                      ) => {
                                                                                          if (
                                                                                              nextValue ===
                                                                                                  'greater_than' ||
                                                                                              nextValue ===
                                                                                                  'less_than'
                                                                                          ) {
                                                                                              applyStarRatingFilter(
                                                                                                  {
                                                                                                      operator:
                                                                                                          nextValue,
                                                                                                      value: starRatingFilterValue.value,
                                                                                                  },
                                                                                              );
                                                                                          }
                                                                                      }}
                                                                                  >
                                                                                      <DropdownMenuRadioItem value="greater_than">
                                                                                          greater
                                                                                          than
                                                                                      </DropdownMenuRadioItem>
                                                                                      <DropdownMenuRadioItem value="less_than">
                                                                                          less
                                                                                          than
                                                                                      </DropdownMenuRadioItem>
                                                                                  </DropdownMenuRadioGroup>
                                                                              </DropdownMenuContent>
                                                                          </DropdownMenu>
                                                                      ) : null}
                                                                      <Popover
                                                                          open={
                                                                              open
                                                                          }
                                                                          onOpenChange={
                                                                              onOpenChange
                                                                          }
                                                                      >
                                                                          <PopoverTrigger
                                                                              asChild={
                                                                                  true
                                                                              }
                                                                          >
                                                                              <Button
                                                                                  variant="outline"
                                                                                  size="sm"
                                                                                  className="flex h-7 max-w-[16rem] min-w-[3rem] items-center gap-1 truncate rounded-full px-3 text-xs"
                                                                                  title={
                                                                                      starRatingValueLabel ??
                                                                                      'Any rating'
                                                                                  }
                                                                              >
                                                                                  <span className="flex-1 truncate text-left">
                                                                                      {starRatingValueLabel ??
                                                                                          'Any rating'}
                                                                                  </span>
                                                                                  <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                                                                              </Button>
                                                                          </PopoverTrigger>
                                                                          <PopoverContent
                                                                              className="w-48 p-2"
                                                                              align="start"
                                                                          >
                                                                              <StarRatingComparisonFilterMenu
                                                                                  filterValue={
                                                                                      starRatingFilterValue
                                                                                  }
                                                                                  applyButtonLabel="Apply star rating value"
                                                                                  hideOperatorSelector={
                                                                                      true
                                                                                  }
                                                                                  inputAriaLabel="Star rating filter value"
                                                                                  lockedOperator={
                                                                                      starRatingFilterValue.operator ??
                                                                                      'greater_than'
                                                                                  }
                                                                                  onApply={(
                                                                                      value,
                                                                                  ) => {
                                                                                      applyStarRatingFilter(
                                                                                          value,
                                                                                      );
                                                                                      onOpenChange(
                                                                                          false,
                                                                                      );
                                                                                  }}
                                                                                  onClear={() => {
                                                                                      clearStarRatingFilter();
                                                                                      onOpenChange(
                                                                                          false,
                                                                                      );
                                                                                  }}
                                                                              />
                                                                          </PopoverContent>
                                                                      </Popover>
                                                                  </div>
                                                              ),
                                                          children: (
                                                              <StarRatingComparisonFilterMenu
                                                                  filterValue={
                                                                      starRatingFilterValue
                                                                  }
                                                                  onApply={(
                                                                      value,
                                                                  ) => {
                                                                      applyStarRatingFilter(
                                                                          value,
                                                                      );
                                                                  }}
                                                                  onClear={
                                                                      clearStarRatingFilter
                                                                  }
                                                              />
                                                          ),
                                                          activeFilterLabels:
                                                              starRatingValueLabel
                                                                  ? [
                                                                        starRatingValueLabel,
                                                                    ]
                                                                  : [],
                                                          onClear:
                                                              clearStarRatingFilter,
                                                      },
                                                  ]
                                                : []),
                                            {
                                                id: 'schedule_availability',
                                                title: 'Scheduled availability',
                                                icon: CalendarCheck,
                                                operatorLabel: '',
                                                selectedCount:
                                                    scheduleAvailabilityValue ===
                                                    'all'
                                                        ? 0
                                                        : 1,
                                                renderValueSelection: ({
                                                    open,
                                                    onOpenChange,
                                                }) => (
                                                    <Popover
                                                        open={open}
                                                        onOpenChange={
                                                            onOpenChange
                                                        }
                                                    >
                                                        <PopoverTrigger
                                                            asChild={true}
                                                        >
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-7 max-w-[16rem] min-w-[6rem] truncate rounded-full px-3 text-xs"
                                                            >
                                                                {
                                                                    scheduleAvailabilityLabel
                                                                }
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent
                                                            className="w-48 p-1"
                                                            align="start"
                                                        >
                                                            {[
                                                                ['all', 'All'],
                                                                [
                                                                    'available',
                                                                    'Available',
                                                                ],
                                                                [
                                                                    'has_shift_conflict',
                                                                    'Has conflict',
                                                                ],
                                                            ].map(
                                                                ([
                                                                    value,
                                                                    label,
                                                                ]) => (
                                                                    <Button
                                                                        key={
                                                                            value
                                                                        }
                                                                        type="button"
                                                                        variant="ghost"
                                                                        className="flex h-8 w-full items-center justify-between px-2 text-sm"
                                                                        onClick={() => {
                                                                            setRadioColumnFilterValue(
                                                                                'schedule_availability',
                                                                                value,
                                                                            );
                                                                            onOpenChange(
                                                                                false,
                                                                            );
                                                                        }}
                                                                    >
                                                                        <span>
                                                                            {
                                                                                label
                                                                            }
                                                                        </span>
                                                                        {scheduleAvailabilityValue ===
                                                                        value ? (
                                                                            <Check className="h-4 w-4" />
                                                                        ) : null}
                                                                    </Button>
                                                                ),
                                                            )}
                                                        </PopoverContent>
                                                    </Popover>
                                                ),
                                                children: (
                                                    <DropdownMenuRadioGroup
                                                        value={
                                                            scheduleAvailabilityValue
                                                        }
                                                        onValueChange={(
                                                            value,
                                                        ) => {
                                                            setRadioColumnFilterValue(
                                                                'schedule_availability',
                                                                value,
                                                            );
                                                        }}
                                                    >
                                                        <DropdownMenuRadioItem value="all">
                                                            All
                                                        </DropdownMenuRadioItem>
                                                        <DropdownMenuRadioItem value="available">
                                                            Available
                                                        </DropdownMenuRadioItem>
                                                        <DropdownMenuRadioItem value="has_shift_conflict">
                                                            Has conflict
                                                        </DropdownMenuRadioItem>
                                                    </DropdownMenuRadioGroup>
                                                ),
                                                activeFilterLabels:
                                                    scheduleAvailabilityValue ===
                                                    'all'
                                                        ? []
                                                        : [
                                                              formatRecommendationDefaultOptionLabel(
                                                                  'schedule_availability',
                                                                  scheduleAvailabilityValue,
                                                              ),
                                                          ],
                                                onClear: () => {
                                                    clearRadioColumnFilter(
                                                        'schedule_availability',
                                                    );
                                                },
                                            },
                                            {
                                                id: 'overtime_status',
                                                title: 'OT Status',
                                                icon: AlertTriangle,
                                                operatorLabel: '',
                                                selectedCount:
                                                    overtimeStatusValue ===
                                                    'all'
                                                        ? 0
                                                        : 1,
                                                renderValueSelection: ({
                                                    open,
                                                    onOpenChange,
                                                }) => (
                                                    <Popover
                                                        open={open}
                                                        onOpenChange={
                                                            onOpenChange
                                                        }
                                                    >
                                                        <PopoverTrigger
                                                            asChild={true}
                                                        >
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-7 max-w-[16rem] min-w-[6rem] truncate rounded-full px-3 text-xs"
                                                            >
                                                                {
                                                                    overtimeStatusLabel
                                                                }
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent
                                                            className="w-48 p-1"
                                                            align="start"
                                                        >
                                                            {[
                                                                ['all', 'All'],
                                                                [
                                                                    'within_limit',
                                                                    'Within limit',
                                                                ],
                                                                [
                                                                    'overtime',
                                                                    'Overtime',
                                                                ],
                                                            ].map(
                                                                ([
                                                                    value,
                                                                    label,
                                                                ]) => (
                                                                    <Button
                                                                        key={
                                                                            value
                                                                        }
                                                                        type="button"
                                                                        variant="ghost"
                                                                        className="flex h-8 w-full items-center justify-between px-2 text-sm"
                                                                        onClick={() => {
                                                                            setRadioColumnFilterValue(
                                                                                'overtime_status',
                                                                                value,
                                                                            );
                                                                            onOpenChange(
                                                                                false,
                                                                            );
                                                                        }}
                                                                    >
                                                                        <span>
                                                                            {
                                                                                label
                                                                            }
                                                                        </span>
                                                                        {overtimeStatusValue ===
                                                                        value ? (
                                                                            <Check className="h-4 w-4" />
                                                                        ) : null}
                                                                    </Button>
                                                                ),
                                                            )}
                                                        </PopoverContent>
                                                    </Popover>
                                                ),
                                                children: (
                                                    <DropdownMenuRadioGroup
                                                        value={
                                                            overtimeStatusValue
                                                        }
                                                        onValueChange={(
                                                            value,
                                                        ) => {
                                                            setRadioColumnFilterValue(
                                                                'overtime_status',
                                                                value,
                                                            );
                                                        }}
                                                    >
                                                        <DropdownMenuRadioItem value="all">
                                                            All
                                                        </DropdownMenuRadioItem>
                                                        <DropdownMenuRadioItem value="within_limit">
                                                            Within limit
                                                        </DropdownMenuRadioItem>
                                                        <DropdownMenuRadioItem value="overtime">
                                                            Overtime
                                                        </DropdownMenuRadioItem>
                                                    </DropdownMenuRadioGroup>
                                                ),
                                                activeFilterLabels:
                                                    overtimeStatusValue ===
                                                    'all'
                                                        ? []
                                                        : [
                                                              formatRecommendationDefaultOptionLabel(
                                                                  'overtime_status',
                                                                  overtimeStatusValue,
                                                              ),
                                                          ],
                                                onClear: () => {
                                                    clearRadioColumnFilter(
                                                        'overtime_status',
                                                    );
                                                },
                                            },
                                            {
                                                id: 'gender',
                                                title: 'Gender',
                                                icon: User,
                                                operatorLabel: '',
                                                selectedCount:
                                                    genderValue === 'all'
                                                        ? 0
                                                        : 1,
                                                renderValueSelection: ({
                                                    open,
                                                    onOpenChange,
                                                }) => (
                                                    <Popover
                                                        open={open}
                                                        onOpenChange={
                                                            onOpenChange
                                                        }
                                                    >
                                                        <PopoverTrigger
                                                            asChild={true}
                                                        >
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-7 max-w-[16rem] min-w-[6rem] truncate rounded-full px-3 text-xs"
                                                            >
                                                                {genderLabel}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent
                                                            className="w-48 p-1"
                                                            align="start"
                                                        >
                                                            {[
                                                                ['all', 'All'],
                                                                [
                                                                    'female',
                                                                    'Female',
                                                                ],
                                                                [
                                                                    'male',
                                                                    'Male',
                                                                ],
                                                            ].map(
                                                                ([
                                                                    value,
                                                                    label,
                                                                ]) => (
                                                                    <Button
                                                                        key={
                                                                            value
                                                                        }
                                                                        type="button"
                                                                        variant="ghost"
                                                                        className="flex h-8 w-full items-center justify-between px-2 text-sm"
                                                                        onClick={() => {
                                                                            setRadioColumnFilterValue(
                                                                                'gender',
                                                                                value,
                                                                            );
                                                                            onOpenChange(
                                                                                false,
                                                                            );
                                                                        }}
                                                                    >
                                                                        <span>
                                                                            {
                                                                                label
                                                                            }
                                                                        </span>
                                                                        {genderValue ===
                                                                        value ? (
                                                                            <Check className="h-4 w-4" />
                                                                        ) : null}
                                                                    </Button>
                                                                ),
                                                            )}
                                                        </PopoverContent>
                                                    </Popover>
                                                ),
                                                children: (
                                                    <DropdownMenuRadioGroup
                                                        value={genderValue}
                                                        onValueChange={(
                                                            value,
                                                        ) => {
                                                            setRadioColumnFilterValue(
                                                                'gender',
                                                                value,
                                                            );
                                                        }}
                                                    >
                                                        <DropdownMenuRadioItem value="all">
                                                            All
                                                        </DropdownMenuRadioItem>
                                                        <DropdownMenuRadioItem value="female">
                                                            Female
                                                        </DropdownMenuRadioItem>
                                                        <DropdownMenuRadioItem value="male">
                                                            Male
                                                        </DropdownMenuRadioItem>
                                                    </DropdownMenuRadioGroup>
                                                ),
                                                activeFilterLabels:
                                                    genderValue === 'all'
                                                        ? []
                                                        : [genderLabel],
                                                onClear: () => {
                                                    clearRadioColumnFilter(
                                                        'gender',
                                                    );
                                                },
                                            },
                                        ];
                                    const renderedCustomFilters =
                                        customFilters.filter(
                                            (filter) =>
                                                filter.id !==
                                                    'schedule_availability' &&
                                                filter.id !==
                                                    'overtime_status' &&
                                                filter.id !== 'gender',
                                        );

                                    return (
                                        <div className="flex min-w-0 flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-between">
                                            <div className="min-w-0 flex-1 max-md:[&>div]:gap-3">
                                                <DataTableToolbar
                                                    table={table}
                                                    hideGlobalSearch={true}
                                                    searchInput={
                                                        isSearchInToolbar
                                                            ? searchInput
                                                            : undefined
                                                    }
                                                    mobileCompact={true}
                                                    iconOnlyClear={uiV3}
                                                    enableColumnReorder={true}
                                                    customFilters={
                                                        renderedCustomFilters
                                                    }
                                                    extraButtons={
                                                        showSelectedButton
                                                    }
                                                    facetedFilters={[
                                                        {
                                                            columnId:
                                                                'availability_preference',
                                                            title: 'Preferred availability',
                                                            icon: Filter,
                                                            predefinedOptions:
                                                                V2_AVAILABILITY_PREFERENCE_OPTIONS,
                                                            valueFormatter: (
                                                                val,
                                                            ) =>
                                                                formatRecommendationDefaultOptionLabel(
                                                                    'availability_preference',
                                                                    String(
                                                                        val ??
                                                                            '',
                                                                    ),
                                                                ),
                                                        },
                                                        {
                                                            columnId:
                                                                'caregiver_tags',
                                                            title: 'Tags',
                                                            icon: TagsIcon,
                                                            limit: 100,
                                                            valueKind: 'array',
                                                            defaultIncludeMode:
                                                                'all',
                                                            predefinedOptions:
                                                                facetOptions?.caregiver_tags,
                                                        },
                                                        {
                                                            columnId:
                                                                'primary_language',
                                                            title: 'Language',
                                                            icon: Globe,
                                                            valueFormatter: (
                                                                val,
                                                            ) =>
                                                                formatLanguageName(
                                                                    String(
                                                                        val ??
                                                                            '',
                                                                    ) || null,
                                                                ),
                                                            predefinedOptions:
                                                                facetOptions?.primary_language,
                                                        },
                                                        {
                                                            columnId: 'roles',
                                                            title: 'License',
                                                            icon: Briefcase,
                                                            valueKind: 'array',
                                                            valueFormatter: (
                                                                val,
                                                            ) =>
                                                                formatRecommendationDefaultOptionLabel(
                                                                    'roles',
                                                                    String(
                                                                        val ??
                                                                            '',
                                                                    ),
                                                                ),
                                                            predefinedOptions:
                                                                facetOptions?.roles,
                                                        },
                                                        {
                                                            columnId:
                                                                'postal_code',
                                                            title: 'Zip code',
                                                            icon: MapPin,
                                                            predefinedOptions:
                                                                facetOptions?.postal_code,
                                                        },
                                                        {
                                                            columnId:
                                                                'office_name',
                                                            title: 'Office',
                                                            icon: Building,
                                                            limit: 50,
                                                            predefinedOptions:
                                                                facetOptions?.office_name,
                                                        },
                                                        ...[
                                                            {
                                                                columnId:
                                                                    'gender',
                                                                title: 'Gender',
                                                                icon: User,
                                                                predefinedOptions:
                                                                    facetOptions?.gender,
                                                                valueFormatter:
                                                                    (
                                                                        val: unknown,
                                                                    ) => {
                                                                        const value =
                                                                            String(
                                                                                val ??
                                                                                    '',
                                                                            );
                                                                        return value
                                                                            ? value
                                                                                  .charAt(
                                                                                      0,
                                                                                  )
                                                                                  .toUpperCase() +
                                                                                  value.slice(
                                                                                      1,
                                                                                  )
                                                                            : '—';
                                                                    },
                                                            },
                                                            {
                                                                columnId:
                                                                    'schedule_availability',
                                                                title: 'Scheduled availability',
                                                                icon: CalendarCheck,
                                                                predefinedOptions:
                                                                    V2_SCHEDULE_AVAILABILITY_OPTIONS,
                                                                valueFormatter:
                                                                    (
                                                                        val: unknown,
                                                                    ) =>
                                                                        formatRecommendationDefaultOptionLabel(
                                                                            'schedule_availability',
                                                                            String(
                                                                                val ??
                                                                                    '',
                                                                            ),
                                                                        ),
                                                            },
                                                            {
                                                                columnId:
                                                                    'overtime_status',
                                                                title: 'OT Status',
                                                                icon: AlertTriangle,
                                                                predefinedOptions:
                                                                    V2_OVERTIME_STATUS_OPTIONS,
                                                                valueFormatter:
                                                                    (
                                                                        val: unknown,
                                                                    ) =>
                                                                        formatRecommendationDefaultOptionLabel(
                                                                            'overtime_status',
                                                                            String(
                                                                                val ??
                                                                                    '',
                                                                            ),
                                                                        ),
                                                            },
                                                            {
                                                                columnId:
                                                                    'is_preferred',
                                                                title: 'Preferred',
                                                                icon: Star,
                                                                predefinedOptions:
                                                                    V2_PREFERRED_OPTIONS,
                                                                valueFormatter:
                                                                    () =>
                                                                        'Preferred',
                                                            },
                                                        ],
                                                    ]}
                                                />
                                            </div>
                                            <div className="flex shrink-0 items-center gap-2">
                                                {areDefaultsApplied ? (
                                                    <span className="text-muted-foreground text-xs">
                                                        {/* v3's automatic mode
                                                            no longer uses the
                                                            org defaults; they
                                                            only seed manual
                                                            selection. */}
                                                        {isV3BornOutreach
                                                            ? 'Manual default filters applied'
                                                            : 'Defaults applied'}
                                                    </span>
                                                ) : (
                                                    <React.Fragment>
                                                        <Badge variant="secondary">
                                                            Modified
                                                        </Badge>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={
                                                                handleResetToOrgDefaults
                                                            }
                                                        >
                                                            {isV3BornOutreach
                                                                ? 'Reset to manual defaults'
                                                                : 'Reset to defaults'}
                                                        </Button>
                                                    </React.Fragment>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }}
                                onRowSelectionChange={(newSelection) => {
                                    if (isAgentRunning) {
                                        return;
                                    }
                                    setUserSelectedCaregiverIds((previous) => {
                                        const newSelectedIds = new Set(
                                            previous,
                                        );

                                        recommendations.forEach(
                                            (rec, index) => {
                                                const key = index.toString();
                                                const wasSelected =
                                                    rowSelection[key];
                                                const isSelected = (
                                                    newSelection as RowSelectionState
                                                )[key];

                                                if (
                                                    wasSelected &&
                                                    !isSelected
                                                ) {
                                                    newSelectedIds.delete(
                                                        rec.caregiver_id,
                                                    );
                                                } else if (
                                                    !wasSelected &&
                                                    isSelected &&
                                                    canSelectCaregiver(
                                                        rec.caregiver_id,
                                                    )
                                                ) {
                                                    newSelectedIds.add(
                                                        rec.caregiver_id,
                                                    );
                                                }
                                            },
                                        );

                                        return newSelectedIds;
                                    });
                                }}
                                onRowClick={(rec) => {
                                    if (isAgentRunning) {
                                        return;
                                    }
                                    if (
                                        disabledCaregiverIds.includes(
                                            rec.caregiver_id,
                                        )
                                    ) {
                                        return;
                                    }
                                    setUserSelectedCaregiverIds((prev) => {
                                        const newSet = new Set(prev);
                                        if (newSet.has(rec.caregiver_id)) {
                                            newSet.delete(rec.caregiver_id);
                                        } else if (
                                            canSelectCaregiver(rec.caregiver_id)
                                        ) {
                                            newSet.add(rec.caregiver_id);
                                        }
                                        return newSet;
                                    });
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {organizationId ? (
                <CaregiverDetailModal
                    isOpen={selectedCaregiverDetailId !== null}
                    onClose={() => setSelectedCaregiverDetailId(null)}
                    organizationId={organizationId}
                    caregiverId={selectedCaregiverDetailId ?? undefined}
                />
            ) : null}
        </div>
    );
}
